/* ==========================================================================
   ROOMLIFT 2026 — motion layer
   Scroll reveal: fade + 12px up, 60ms stagger, fires ONCE. Nothing
   re-animates on scroll-up, nothing parallaxes, nothing auto-plays.
   Spring curves live in roomlift-2026.css (--rl-spring).

   Morph-safe: Horizon re-renders whole sections on filter/pagination, so
   prime() is idempotent and re-runs on shopify:section:load.
   ========================================================================== */
(function () {
  'use strict';

  /* 2026-09-20: these two conditions used to `return` from the WHOLE file, which also switched
     off the sticky buy bar, the spec-row filter and the thumbnail fix for every visitor with
     Reduce Motion on (and in the theme editor). They now gate the scroll-reveal only. */
  var NO_REVEAL = document.documentElement.classList.contains('shopify-design-mode') ||
                  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var TARGETS = [
    '.rl-tile',
    '.rl-cat',
    '.rl-proof',
    '.rl-section > .rl-wrap > h2',
    '.rl-trade > *',
    '.rl-bundle__item'
  ].join(',');

  var STAGGER_MS = 60;

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var el = entry.target;
      var i = Number(el.dataset.rlI || 0);
      setTimeout(function () { el.classList.add('is-in'); }, i * STAGGER_MS);
      observer.unobserve(el);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.15 });

  function prime() {
    if (NO_REVEAL) return;
    var groups = new Map();
    document.querySelectorAll(TARGETS).forEach(function (el) {
      if (el.classList.contains('rl-reveal')) return;   // already primed

      var parent = el.parentElement || document.body;
      var i = groups.get(parent) || 0;
      groups.set(parent, i + 1);
      el.dataset.rlI = String(Math.min(i, 8));
      el.classList.add('rl-reveal');

      // anything already on screen at load reveals immediately, with the same
      // small cascade, so the page composes instead of popping
      if (el.getBoundingClientRect().top < window.innerHeight * 0.9) {
        setTimeout(function () { el.classList.add('is-in'); }, Math.min(i, 8) * STAGGER_MS);
      } else {
        observer.observe(el);
      }
    });
  }

  // FAILSAFE: a decorative reveal must never be able to hide content for good.
  // If the observer hasn't fired for something within 3s (off-screen stalls,
  // observer never constructed, JS error elsewhere on the page), reveal
  // everything unconditionally.
  function revealAll() {
    document.querySelectorAll('.rl-reveal:not(.is-in)').forEach(function (el) {
      el.classList.add('is-in');
    });
  }


  /* ---------------------------------------------------------------------
     Spec-table filter (added 2026-08-19, shopper walk item 2)

     HomeRoots' "Include Product Specifications in the Description" setting is
     ON deliberately — the polish copy needs the real dimensions from it. But
     it injects the supplier's full logistics block, so shoppers were seeing
     internal warehouse fields: Ship Palletized, Number Of Cartons, per-carton
     weights and dimensions, Shipping Method, GTIN and a meaningless
     "Vanity: Yes".

     We strip those rows on render. "Country Of Manufacture" is also stripped
     by user decision 2026-08-19 (Claude had recommended keeping it; the user
     chose to remove it from every product).

     ⚠ This is a DISPLAY filter. The text still exists in the product
     description HTML, so it remains in page source and may already be indexed.
     A permanent removal means editing the 130 product descriptions.

     Row labels are matched loosely (lowercased, trailing colon and digits
     dropped) so "Shipping Weight Carton 1..7" all collapse to one rule.
  --------------------------------------------------------------------- */
  var SPEC_DROP = [
    'ship palletized',
    'number of cartons',
    'shipping method',
    'gtin (upc/ean)',
    'gtin',
    'vanity',
    'shipping weight carton',
    'shipping height carton',
    'shipping width carton',
    'shipping depth carton',
    'country of manufacture'
  ];

  function normLabel(t) {
    return (t || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/[:\s]+$/, '')
      .replace(/\s*\d+$/, '')   // "shipping weight carton 3" -> "shipping weight carton"
      .trim();
  }

  function filterSpecTables(root) {
    var tables = (root || document).querySelectorAll('table');
    for (var t = 0; t < tables.length; t++) {
      var rows = tables[t].querySelectorAll('tr');
      for (var r = 0; r < rows.length; r++) {
        var cell = rows[r].children[0];
        if (!cell) continue;
        var label = normLabel(cell.textContent);
        if (!label) continue;
        for (var d = 0; d < SPEC_DROP.length; d++) {
          if (label === SPEC_DROP[d] || label.indexOf(SPEC_DROP[d]) === 0) {
            rows[r].remove();
            break;
          }
        }
      }
    }
  }

  /* ---- 5d item 7: mobile sticky ATC. The bar itself is server-rendered in
     the product template (block rl_after_atc) so price/title are Liquid-real;
     this only toggles it when the true buy button leaves the viewport, and
     forwards the click to the real form submit.

     REWRITTEN 2026-09-16 (conversion check). The old version wired an
     IntersectionObserver onto whatever `[type=submit]` it found at
     DOMContentLoaded and never re-wired. Horizon morphs the product section
     on every variant/cart render, so the observed node was replaced by a
     detached twin and the callback stopped firing — `is-on` was never added
     and the bar was invisible on mobile for the whole life of the store
     (measured 0 cart adds in 153 sessions). Two changes make it morph-proof:
     the real button is re-resolved on every evaluation rather than captured,
     and position is read from a throttled scroll listener instead of an
     observer bound to one node. Clicks are delegated off the bar so a
     replaced button still works. ---- */
  function realBuyBtn() {
    /* Only the in-page buy button counts. Quick-adds in the cross-sell grid
       and any fixed bar (ours, or Horizon's native one) must never be the
       scroll anchor — that was the other half of the old bug. */
    var all = document.querySelectorAll('form[action*="/cart/add"] [type="submit"]');
    for (var i = 0; i < all.length; i++) {
      var b = all[i];
      if (b.closest('.rl-sticky-atc, .sticky-add-to-cart')) continue;
      if (b.closest('.product-form-buttons')) return b;
    }
    return null;
  }

  /* 2026-09-20 review: the listeners are bound ONCE for the life of the page and always act on the
     newest bar - the theme editor replaces the bar on every re-render, and a per-element flag leaked a
     new set of scroll listeners each time. */
  var liveBar = null, stickyBound = false;
  function drawerWillOpen() { return !!document.querySelector('cart-drawer-component[auto-open]'); }
  function bringIntoView(el) {
    /* the cart drawer pins the page at whatever scroll offset it reads; a smooth scroll still in
       flight made closing the drawer drop the buyer at a random position */
    if (drawerWillOpen()) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  function stickyAtc() {
    var bar = document.querySelector('.rl-sticky-atc');
    if (!bar) return;
    liveBar = bar;

    if (!bar.dataset.rlWired) {
      bar.dataset.rlWired = '1';

      /* Delegated: survives Horizon replacing the buttons underneath us. */
      bar.addEventListener('click', function (e) {
        var add = e.target.closest('.rl-sticky-atc__btn');
        var buy = e.target.closest('.rl-sticky-atc__buy');
        if (!add && !buy) return;
        var real = realBuyBtn();
        if (!real) return;
        if (add) {
          real.click();
          bringIntoView(real);
          return;
        }
        /* Buy now: add, then go straight to checkout. Falls back to a plain
           add-to-cart if the request fails, so the click is never dead. */
        var form = real.closest('form');
        if (!form) { real.click(); return; }
        buy.setAttribute('aria-busy', 'true');
        var addUrl = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add';
        if (!/\.js$/.test(addUrl)) addUrl += '.js';   // same route Horizon uses, so a market/locale prefix is respected
        fetch(addUrl, {
          method: 'POST',
          body: new FormData(form),
          headers: { Accept: 'application/json' }
        }).then(function (r) {
          if (!r.ok) throw new Error('add failed');
          window.location.href = '/checkout';
        }).catch(function () {
          buy.removeAttribute('aria-busy');
          real.click();
          bringIntoView(real);
        });
      });
    }

    /* 2026-09-20: the bar used to offer ADD TO CART / BUY NOW on sold-out pieces (both dead).
       It now mirrors the real button: disabled, same label, Buy now hidden. */
    function syncSoldOut(real) {
      var bar = liveBar; if (!bar) return;
      var off = !!real.disabled || real.getAttribute('aria-disabled') === 'true';
      if (bar.dataset.rlSoldout === String(off)) return;
      bar.dataset.rlSoldout = String(off);
      bar.classList.toggle('is-soldout', off);
      var add = bar.querySelector('.rl-sticky-atc__btn');
      var buy = bar.querySelector('.rl-sticky-atc__buy');
      if (add) {
        if (!add.dataset.rlLabel) add.dataset.rlLabel = add.textContent;
        add.disabled = off;
        add.textContent = off ? ((real.textContent || '').replace(/\s+/g, ' ').trim() || 'Sold out') : add.dataset.rlLabel;
      }
      if (buy) { buy.hidden = off; buy.disabled = off; }
    }

    var ticking = false;
    function evaluate() {
      ticking = false;
      var real = realBuyBtn();
      if (!real) return;
      var bar = liveBar; if (!bar) return;
      syncSoldOut(real);
      /* keep the bar's price in step with the selected variant (it was rendered once, server-side) */
      var livePrice = document.querySelector('product-price .price'), barPrice = bar.querySelector('.rl-sticky-atc__price');
      var money = livePrice && (livePrice.textContent.match(/[$\u00a3\u20ac]\s?[\d,]+(?:\.\d{2})?/) || [])[0];
      if (money && barPrice && barPrice.textContent.trim() !== money) barPrice.textContent = money;
      var r = real.getBoundingClientRect();
      /* On once the real button's bottom has passed above the fold, and it
         STAYS on all the way down the page — the cross-sell grid and the
         spec table are exactly where a considering shopper needs it.
         2026-10-07 (fix9 follow-up, owner "both"): ALSO on while the real button is still BELOW the
         first screen (r.top > innerHeight) - with the square phone gallery the real button starts under
         the fold at 390x664, which left no buy button on screen at load. Off while the real button is in
         the screen (top <= innerHeight and bottom >= 40). Applies to every product page.
         2026-10-07 fix12: on whenever the real button is not FULLY on screen (r.bottom > innerHeight), so a
         page whose button peeks in at the bottom edge (Wendover at 390x664 after the price band) never shows
         a sliver with no bar. Off only while the whole button is visible. */
      bar.classList.toggle('is-on', r.bottom < 40 || r.bottom > window.innerHeight);
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(evaluate);
    }
    if (!stickyBound) {
      stickyBound = true;
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      document.addEventListener('variant:update', onScroll);   // a colour can be sold out while its sibling is not
    }
    evaluate();
  }

  /* ---- 2026-09-20 collections (m9): the grid loads 24 at a time by infinite scroll and gave no sense
     of how much was left or when it ended. One quiet line under the grid: "Showing 24 of 42", then
     "That is all 42 pieces." The total is read from the toolbar count Horizon already keeps in step
     with the filters; the observer sits on <results-list> so it survives Horizon morphing the grid. ---- */
  var gridEndHost = null;
  function gridEnd() {
    if (location.pathname.indexOf('/collections/') === -1) return;   // not on /search: its total can include pages
    var host = document.querySelector('results-list');
    if (!host) return;
    function paint() {
      if (!host.isConnected) return;
      var grid = host.querySelector('.product-grid');
      if (!grid) return;
      var countEl = host.querySelector('.products-count-wrapper [role="status"], .rl-count-mobile');
      var m = countEl && countEl.textContent.match(/\d[\d,]*/);
      var total = m ? parseInt(m[0].replace(/,/g, ''), 10) : 0;
      var shown = grid.querySelectorAll('.product-grid__item').length;
      var note = host.querySelector('.rl-grid-end');
      if (!note) {
        note = document.createElement('p');
        note.className = 'rl-grid-end';
        note.setAttribute('role', 'status');
        grid.insertAdjacentElement('afterend', note);
      }
      var text = '';
      if (total > 0 && shown < total) text = 'Showing ' + shown + ' of ' + total;
      else if (total > 24 && shown >= total) text = 'That is all ' + total + ' pieces.';
      if (note.textContent !== text) note.textContent = text;
    }
    paint();
    if (gridEndHost === host) return;   // one observer per <results-list>; the theme editor can replace it
    gridEndHost = host;
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      setTimeout(function () { queued = false; paint(); }, 120);
    }).observe(host, { childList: true, subtree: true });
  }

  /* ---- 2026-09-20 cart page (m6): on a phone the Check out button leaves the first screen as soon as
     the cart holds a third line or a freight notice, and it is gone for good once the buyer scrolls
     into "You may also like". A fixed bar carries the live total and one Check out button whenever the
     real button is off screen. The bar submits nothing itself - a tap clicks the REAL button, so the
     cart form, discount codes and Horizon's own handlers behave exactly as they do today. Horizon
     morphs the cart section on every quantity change, so the real button and the total are re-resolved
     on every evaluation and never captured.
     Hardened after an adversarial review (same day): the bar stands down while any dialog/drawer is
     open and while the buyer is typing in the cart (the phone keyboard shrinks the viewport, which
     would otherwise park the bar on top of the discount field); one tap = one submit; the observer is
     re-bound if the theme editor replaces the section. ---- */
  var cartBarBound = false, cartBarScope = null, cartBarObserver = null;
  function realCheckoutBtn() {
    var all = document.querySelectorAll('main .cart-page button[name="checkout"]');
    for (var i = 0; i < all.length; i++) {
      if (all[i].getClientRects().length) return all[i];   // rendered (the empty-cart template copy is not)
    }
    return null;
  }
  function stickyCheckout() {
    if (!document.querySelector('main .cart-page')) return;   // cart template only - never the drawer
    var bar = document.querySelector('.rl-sticky-co');
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'rl-sticky-co';
      bar.setAttribute('role', 'region');
      bar.setAttribute('aria-label', 'Check out');
      bar.innerHTML =
        '<p class="rl-sticky-co__meta"><span class="rl-sticky-co__label">Estimated total</span>' +
        '<span class="rl-sticky-co__total"></span></p>' +
        '<button type="button" class="rl-sticky-co__btn">Check out</button>';
      document.body.appendChild(bar);
      bar.addEventListener('click', function (e) {
        var btn = e.target.closest('.rl-sticky-co__btn');
        if (!btn || btn.disabled) return;
        var real = realCheckoutBtn();
        if (!real || real.disabled) return;
        btn.disabled = true;                                   // one tap = one submit
        btn.setAttribute('aria-busy', 'true');
        setTimeout(function () { btn.disabled = false; btn.removeAttribute('aria-busy'); }, 8000);   // the page stayed (validation, offline)
        real.click();
      });
      /* back/forward cache: returning from checkout must not leave the button disabled */
      window.addEventListener('pageshow', function () {
        var b = document.querySelector('.rl-sticky-co__btn');
        if (b) { b.disabled = false; b.removeAttribute('aria-busy'); }
      });
    }
    var ticking = false;
    function standDown() {
      if (document.querySelector('dialog[open]')) return true;                       // cart drawer, search, menu
      var a = document.activeElement;
      return !!(a && a.closest && a.closest('main .cart-page input, main .cart-page textarea, main .cart-page select'));
    }
    function evaluate() {
      ticking = false;
      var bar = document.querySelector('.rl-sticky-co');
      if (!bar) return;
      var real = realCheckoutBtn();
      if (!real || real.disabled || standDown()) { bar.classList.remove('is-on'); return; }   // empty cart: no bar
      var totalEl = document.querySelector('main .cart-page .cart-totals__total-value');
      var total = totalEl ? totalEl.textContent.replace(/\s+/g, ' ').trim() : '';
      var out = bar.querySelector('.rl-sticky-co__total');
      if (out && out.textContent !== total) out.textContent = total;
      var r = real.getBoundingClientRect();
      /* on when less than half of the real button is inside the viewport, above or below */
      var off = r.top > window.innerHeight - r.height / 2 || r.bottom < r.height / 2;
      bar.classList.toggle('is-on', off);
    }
    function onChange() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(evaluate);
    }
    if (!cartBarBound) {
      cartBarBound = true;
      window.addEventListener('scroll', onChange, { passive: true });
      window.addEventListener('resize', onChange, { passive: true });
      document.addEventListener('focusin', onChange);
      document.addEventListener('focusout', onChange);
      document.addEventListener('toggle', onChange, true);      // <dialog>/<details> open and close
      document.addEventListener('close', onChange, true);
    }
    /* quantity changes and removals re-render the section: the button moves and the total changes.
       The observed node is tracked, not assumed - the theme editor replaces the whole section. */
    var host = document.querySelector('main .cart-page');
    var scope = (host && host.closest('.shopify-section')) || document.querySelector('main');
    if (scope && scope !== cartBarScope) {
      if (cartBarObserver) cartBarObserver.disconnect();
      cartBarScope = scope;
      cartBarObserver = new MutationObserver(onChange);
      cartBarObserver.observe(scope, { childList: true, subtree: true, characterData: true });
    }
    evaluate();
  }

  /* ---- 2026-09-20 search (m1): "lamp" returns 122 of 137 products. Shopify's storefront search cannot
     be told which fields to search (the /search page ignores options[fields]; only the predictive API
     takes it) and it also matches descriptions and related words. It DOES rank name matches first - the
     21 real lamps come before anything else. So the list is left alone and one full-width divider is
     placed where the name matches stop: above it the pieces called what the buyer typed, below it the
     long tail, labelled for what it is. No divider when nothing matches by name (a typo, a room word)
     or when everything does. The typed words are only ever written with textContent. ---- */
  var searchDivHost = null;
  function searchDivider() {
    if (!/\/search\/?$/.test(location.pathname)) return;
    var host = document.querySelector('results-list');
    if (!host) return;
    var STOP = { the: 1, and: 1, for: 1, with: 1, from: 1, set: 1 };
    /* plurals only: benches -> bench, boxes -> box, tables -> table, mirrors -> mirror; glass and brass stay */
    function stem(w) {
      if (/(?:ch|sh|x|z|ss)es$/.test(w)) return w.slice(0, -2);
      if (/[^s]s$/.test(w)) return w.slice(0, -1);
      return w;
    }
    function words(s) {
      return (s || '').toLowerCase().replace(/[^a-z0-9\s-]/g, ' ').split(/[\s-]+/).filter(Boolean);
    }
    function paint() {
      if (!host.isConnected) return;
      var params = new URLSearchParams(location.search);
      var q = params.get('q') || '';
      var sort = params.get('sort_by') || 'relevance';
      var grid = host.querySelector('.product-grid');
      var old = host.querySelector('.rl-search-divider');
      /* only in relevance order - sorted by price the name matches are scattered and a line would lie */
      var terms = (sort === 'relevance' && q.indexOf(':') === -1) ? words(q).filter(function (w) { return w.length > 2 && !STOP[w]; }).map(stem) : [];
      var at = null;
      if (grid && terms.length) {
        var cards = grid.querySelectorAll('.product-grid__item');
        var seenMatch = false;
        for (var i = 0; i < cards.length; i++) {
          var h = cards[i].querySelector('[role="heading"], .product-card__link .visually-hidden');
          var tw = words(h ? h.textContent : '').map(stem);
          var ok = terms.every(function (t) { return tw.some(function (w) { return w.indexOf(t) === 0; }); });
          if (ok) { seenMatch = true; continue; }
          if (seenMatch) { at = cards[i]; }
          break;                                     // the first piece that is not a name match decides it
        }
      }
      if (!at) { if (old) old.remove(); return; }
      if (old && old.nextElementSibling === at) return;          // already in place
      if (old) old.remove();
      var li = document.createElement('li');
      li.className = 'rl-search-divider';
      li.setAttribute('role', 'presentation');
      var p = document.createElement('p');
      p.textContent = 'Closest matches are above. The pieces below are related to “' + q.trim().slice(0, 60) +
        '” but do not have it in their name.';
      li.appendChild(p);
      at.parentNode.insertBefore(li, at);
    }
    paint();
    if (searchDivHost === host) return;   // one observer per <results-list>
    searchDivHost = host;
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      setTimeout(function () { queued = false; paint(); }, 150);
    }).observe(host, { childList: true, subtree: true });
  }

  /* ---- 2026-09-20 site-wide (D21): the desktop drop-down printed its links under the LOGO (x=40)
     whichever item was hovered. This measures how far each top-level item sits from the logo and writes
     one rule per item into a <style> in <head>, so "Tables" opens under "Tables". It is a stylesheet,
     not inline styles, on purpose: Horizon re-renders a drop-down from the server the first time it is
     hovered, which wipes anything set on the element itself. Capped so the column can never run off
     the right edge. Layout only - Horizon still owns opening, closing and keyboard handling. ---- */
  var menuAlignBound = false;
  function menuAlign() {
    function run() {
      var logo = document.querySelector('.header-logo');
      var base = logo ? logo.getBoundingClientRect().left : 0;
      var items = document.querySelectorAll('header .menu-list__list-item');
      var max = Math.max(0, window.innerWidth - base * 2 - 280);
      var css = '';
      for (var i = 0; i < items.length; i++) {
        var li = items[i];
        if (!li.querySelector('.menu-list__submenu')) continue;
        var link = li.querySelector('.menu-list__link') || li;
        var x = link.getBoundingClientRect().left;
        if (!x || !li.parentNode) continue;                                   // parked in the overflow ("More")
        var n = Array.prototype.indexOf.call(li.parentNode.children, li) + 1;
        var px = Math.round(Math.max(0, Math.min(x - base, max)));
        css += 'header .menu-list__list-item:nth-child(' + n + ') .menu-list__submenu .mega-menu__grid{margin-inline-start:' + px + 'px}';
      }
      var tag = document.getElementById('rl-menu-align');
      if (!tag) { tag = document.createElement('style'); tag.id = 'rl-menu-align'; document.head.appendChild(tag); }
      var next = '@media (min-width:990px){' + css + '}';
      if (tag.textContent !== next) tag.textContent = next;
    }
    run();
    if (menuAlignBound) return;
    menuAlignBound = true;
    var t = null;
    window.addEventListener('resize', function () { clearTimeout(t); t = setTimeout(run, 150); }, { passive: true });
    window.addEventListener('load', run);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(run);   // the nav shifts once Archivo lands
  }

  /* F27 · "Keep shopping" under the cart trust lines. On the CART PAGE the link is an ordinary
     link to /collections/all. Inside the cart DRAWER navigating away would throw the shopper off
     the page they were on, so there the link just closes the drawer. One delegated listener,
     bound once, so Horizon's section re-renders cannot stack it. */
  var cartKeepBound = false;
  function cartKeep() {
    if (cartKeepBound) return;
    cartKeepBound = true;
    document.addEventListener('click', function (e) {
      var a = e.target && e.target.closest && e.target.closest('a.rl-cart-keep');
      if (!a) return;
      var drawer = a.closest('cart-drawer-component');
      if (!drawer) return;                      // cart page: let the link navigate
      var close = drawer.querySelector('.cart-drawer__close-button');
      if (!close) return;                       // no close button found: navigate rather than trap
      e.preventDefault();
      close.click();
    });
  }

  /* DEAL LINE (2026-09-30, CSS section 32): ticks every .rl-deal (2026-10-01 owner: >= 24 h left
     reads "3d 11h 26m", under 24 h "11h 26m 05s" - dealFmt) from its data-end-s (epoch seconds, Liquid) or data-ends (ISO). One shared 1 s ticker that re-queries
     the DOM each tick, so a Horizon morph that swaps the element is picked up on the next tick.
     At <= 0 the line is swapped for the hidden .rl-deal-off marker (its wrapper collapses);
     deal CARDS (.rl-deal-price, data-rl-deal-end-s) drop their Was/Now words at the same moment.
     Nothing ever resets or re-arms a finished deal. */
  var dealTick = null;
  function dealFmt(left) {               // left = whole seconds remaining, > 0
    function pad(n) { return (n < 10 ? '0' : '') + n; }
    var d = Math.floor(left / 86400), h = Math.floor(left % 86400 / 3600), m = Math.floor(left % 3600 / 60), s = left % 60;
    if (left >= 86400) return d + 'd ' + pad(h) + 'h ' + pad(m) + 'm';
    return pad(h) + 'h ' + pad(m) + 'm ' + pad(s) + 's';
  }
  function dealTimer() {
    if (dealTick || !document.querySelector('.rl-deal, .rl-deal-price')) return;
    function run() {
      var now = Date.now();
      document.querySelectorAll('.rl-deal-price').forEach(function (c) {
        if (parseInt(c.getAttribute('data-rl-deal-end-s'), 10) * 1000 <= now) c.classList.replace('rl-deal-price', 'rl-deal-price-over');
      });
      document.querySelectorAll('.rl-deal').forEach(function (el) {
        var end = parseInt(el.getAttribute('data-end-s'), 10) * 1000;
        if (!end) end = Date.parse(el.getAttribute('data-ends') || '');
        var left = Math.floor((end - now) / 1000);
        if (!(left > 0)) { var off = document.createElement('span'); off.className = 'rl-deal-off'; off.hidden = true; el.replaceWith(off); return; }
        var t = el.querySelector('.rl-deal__t');
        if (t) { var txt = dealFmt(left); if (t.textContent !== txt) t.textContent = txt; }
      });
      if (!document.querySelector('.rl-deal, .rl-deal-price')) { clearInterval(dealTick); dealTick = null; }
    }
    dealTick = setInterval(run, 1000);
    run();
  }

  /* WELCOME POP-UP (2026-10-02, owner-approved): 10% off the first order for an email.
     Markup = snippets/rl-welcome.liquid (not rendered for logged-in customers / cart / account /
     contact), CSS section 33. Shows 8.5 s after navigation start on desktop, 5.5 s on a phone
     (<= 749px, owner 2026-10-03); never on /cart, /checkouts,
     /account, /challenge, /policies/, /pages/contact; waits while a drawer, menu or dialog is open.
     localStorage rl_welcome = 'subscribed' (forever) or 'dismissed:<ms>' (quiet for 30 days).
     Submit = fetch POST to the form's own action (/contact); a bot challenge, a network error or an
     unreadable answer falls back to the normal form submit, and the success state is shown when
     the page comes back with form.posted_successfully? AND (session flag rl_welcome_pending OR the
     #rl-wl-form hash the form's own action carries) - even if storage already says 'subscribed' -
     then customer_posted is stripped from the URL. If another script (Shopify's spam-protection
     captcha) already took the submit (defaultPrevented), we do not fetch as well - that double
     submit is what lost the code on 2026-10-02.
     Built by apply_welcome1.py from welcome1.js; since fix7 (2026-10-07) edited here directly.
     FIX7 BUBBLE (2026-10-07, owner "Bubble it is"): the card no longer drops in by itself. After WL_DELAY a walnut
     "%" button #rl-wb appears bottom-left (phone and desktop) with a speech bubble "Claim your 10% off"; the text
     fades after 10 s and is said once per visit (session flag rl_wb_said; later pages show the button alone after
     1.5 s). Tap = the card opens (form, success state, code reveal unchanged) and the button hides; the card's x or
     Escape = back to the button, no storage write; "No thanks" = 'dismissed:<ms>' (30 days).
     FIX7b DRAG (2026-10-07, owner): no visible x on the button. It drags like a chat head (pointer events, < 8 px =
     a tap); while dragging an "x Remove" pill #rl-wb-bin shows at the bottom centre (Android home-screen style)
     and a drop on / within ~40 px of it = 'dismissed:<ms>'; any other drop snaps to the nearest left / right edge at the drop height
     (clamped to the viewport, never on the sticky buy bar), remembered for the visit (sessionStorage rl_wb_pos).
     Keyboard: a "Dismiss" button that stays invisible until it has keyboard focus.
     The button rides above a fixed bottom bar (.rl-sticky-atc / .rl-sticky-co) via --rl-wb-bottom and fades while
     it would sit on the page's buy area (.product-form-buttons). Same path / creator / quiet / busy guards as before. CSS section 33b. */
  var WL_KEY = 'rl_welcome', WL_PEND = 'rl_welcome_pending', WL_QUIET = 30 * 864e5,
      WL_DELAY = 8000, WB_SAID = 'rl_wb_said', WB_POS = 'rl_wb_pos', WB_HUSH = 10000;   // owner 2026-10-07: 8-10 s, phone and desktop alike
  function wlGet(s, k) { try { return window[s].getItem(k); } catch (e) { return undefined; } }
  function wlSet(s, k, v) { try { if (v === null) window[s].removeItem(k); else window[s].setItem(k, v); return true; } catch (e) { return false; } }
  function wlQuiet() {
    var v = wlGet('localStorage', WL_KEY);
    if (!v) return false;
    if (v === 'subscribed') return true;
    var m = /^dismissed:(\d+)$/.exec(v);
    return !!(m && Date.now() - Number(m[1]) < WL_QUIET);
  }
  function wlBadPath() {
    var p = location.pathname.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/)/i, '');
    return /^\/(cart|checkouts?|account|challenge|password)(\/|$)|^\/policies\/|^\/pages\/(contact|creators)(\/|$)/.test(p);
  }
  /* CREATOR VISITORS (2026-10-02, owner-approved fix): a visitor who arrived through a creator's link already has
     that creator's code applied (Shopify's /discount/<CODE> link sets the discount_code cookie). Only one code
     works per checkout, so offering ROOMLIFT10 here would let them swap it in and the creator would lose the sale.
     Once seen, a session flag keeps the pop-up quiet for the rest of the visit. */
  function wlCreator() {
    if (wlGet('sessionStorage', 'rl_creator') === '1') return true;
    /* creator links end their redirect with ?rl=c (e.g. /discount/STEPH10?redirect=/products/x%3Frl%3Dc), which
       works even if the browser hides the discount cookie from scripts */
    if (/[?&]rl=c\b/.test(location.search) || /(^|;\s*)discount_code=[^;]+/.test(document.cookie || '')) {
      wlSet('sessionStorage', 'rl_creator', '1'); return true;
    }
    return false;
  }
  function wlBusy() {
    return !!document.querySelector('dialog[open], details.menu-drawer-container[open], cart-drawer-component[open], .shopify-section-header details[open] .menu-drawer');
  }
  function welcome() {
    var el = document.getElementById('rl-wl');
    if (!el || el.dataset.rlWlInit) return;
    el.dataset.rlWlInit = '1';
    var form = el.querySelector('form'), input = el.querySelector('.rl-wl__in'),
        btn = el.querySelector('.rl-wl__form .rl-wl__btn'), err = el.querySelector('.rl-wl__err'),
        ask = el.querySelector('.rl-wl__ask'), done = el.querySelector('.rl-wl__done'),
        copy = el.querySelector('[data-rl-wl-copy]'), mq = window.matchMedia('(max-width: 749px)'),
        watch = null, shown = false, BTN = btn ? btn.textContent : '';

    function place() {
      if (!mq.matches) { el.style.removeProperty('--rl-wl-top'); return; }
      var h = document.querySelector('header-component') || document.querySelector('.header-section');
      var b = h ? h.getBoundingClientRect().bottom : 0;
      el.style.setProperty('--rl-wl-top', Math.round(Math.max(8, b + 8)) + 'px');
    }
    var placeQ = false;
    function onScroll() { if (placeQ) return; placeQ = true; requestAnimationFrame(function () { placeQ = false; place(); }); }
    function onKey(e) { if (e.key === 'Escape' && shown && !wlBusy()) close(false); }
    function show() {
      if (shown) return; shown = true;
      el.hidden = false; place();
      requestAnimationFrame(function () { requestAnimationFrame(function () { el.classList.add('is-in'); }); });
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      document.addEventListener('keydown', onKey);
      watch = setInterval(function () { el.classList.toggle('is-muted', wlBusy()); }, 400);
    }
    function close(dismiss) {
      if (!shown) return; shown = false;
      if (dismiss && (!done || done.hidden)) wlSet('localStorage', WL_KEY, 'dismissed:' + Date.now());
      var had = el.contains(document.activeElement);
      if (had) document.activeElement.blur();
      el.classList.remove('is-in');
      clearInterval(watch);
      window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll);
      document.removeEventListener('keydown', onKey);
      setTimeout(function () { if (!shown) el.hidden = true; }, 380);
      if (!wlQuiet()) wbShow(false, had);          // back to the button (never after a sign-up or "No thanks")
    }
    function success(focus) {
      wlSet('localStorage', WL_KEY, 'subscribed');
      wlSet('sessionStorage', WL_PEND, 'done');     // kept (not cleared) so a page that comes back from a stray native submit still shows the code
      ask.hidden = true; done.hidden = false;
      el.setAttribute('aria-labelledby', 'rl-wl-h2');
      show();
      if (focus) { var h2 = document.getElementById('rl-wl-h2'); if (h2) h2.focus({ preventScroll: true }); }
    }
    function fail(msg) {
      err.textContent = msg; err.hidden = false;
      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('aria-describedby', 'rl-wl-err rl-wl-legal');
      if (btn) { btn.disabled = false; btn.textContent = BTN; }
    }
    // Fallback = a REAL submit event on the untouched form, with our handler standing aside. Shopify's
    // own storefront-forms script (document submit listener) then adds its form_key, saves the fields
    // for repopulation and posts natively - which is what its bot-check (/challenge) round trip needs.
    // HTMLFormElement.submit() skipped that script, and after the challenge Shopify answered
    // "invalid parameters" (owner's incognito test, 2026-10-02).
    var bypass = false;
    function nativeSubmit() {
      bypass = true;
      if (btn) { btn.disabled = false; btn.textContent = BTN; }
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else if (btn) btn.click();
      else HTMLFormElement.prototype.submit.call(form);
    }

    el.querySelectorAll('[data-rl-wl-close]').forEach(function (b) {
      b.addEventListener('click', function () { close(b.classList.contains('rl-wl__no')); });
    });

    // the corner button (fix7). wb missing (old cached snippet) = the card drops in as before.
    // fix7b drag model: wbPos = { s: 'l' | 'r', b: px from the viewport bottom } once the visitor has moved it.
    var wb = document.getElementById('rl-wb'), wbOpen = wb && wb.querySelector('.rl-wb__open'),
        bin = document.getElementById('rl-wb-bin'),
        wbOn = false, wbWatch = null, wbHushT = null, wbQ = false, wbDrag = null, wbDragged = false, wbPos = null;
    try { wbPos = JSON.parse(wlGet('sessionStorage', WB_POS) || 'null'); } catch (e) { wbPos = null; }
    function wbBarH() {
      var vh = window.innerHeight, h = 0;
      document.querySelectorAll('.rl-sticky-atc, .rl-sticky-co').forEach(function (b) {
        var r = b.getBoundingClientRect();
        if (r.height > 0 && r.top < vh && r.bottom >= vh - 2) h = Math.max(h, vh - r.top);
      });
      return h;
    }
    function wbLift() {
      if (!wbOn || wbDrag) return;
      var vh = window.innerHeight, vw = document.documentElement.clientWidth, h = wbBarH(), hit = false;
      var bot = wbPos ? wbPos.b : 0;                       // 0 = the CSS default (16 px + home-bar inset)
      if (h) bot = Math.max(bot, h + 12);                  // never on the sticky buy bar: 12 px above it
      if (bot) wb.style.setProperty('--rl-wb-bottom', Math.round(Math.min(bot, vh - 56)) + 'px');
      else wb.style.removeProperty('--rl-wb-bottom');
      var right = !!(wbPos && wbPos.s === 'r');
      if (right) wb.style.setProperty('--rl-wb-left', Math.max(16, vw - 64) + 'px'); else wb.style.removeProperty('--rl-wb-left');
      wb.classList.toggle('is-right', right);
      // never sit on the page's own buy area (quantity, Add to cart, Shop Pay, more options) when it passes under the button
      var me = wbOpen.getBoundingClientRect();
      document.querySelectorAll('.product-form-buttons').forEach(function (b) {
        var r = b.getBoundingClientRect();
        if (r.height && r.left < me.right && r.right > me.left && r.top < me.bottom && r.bottom > me.top) hit = true;
      });
      wb.classList.toggle('is-yield', hit);
    }
    function wbOnScroll() { if (wbQ) return; wbQ = true; requestAnimationFrame(function () { wbQ = false; wbLift(); }); }
    function wbHush() { clearTimeout(wbHushT); wb.classList.add('is-hush'); }
    function wbShow(say, focus) {
      if (!wb || wbOn || shown || wlQuiet()) return;
      wbOn = true; wb.hidden = false; wbOpen.setAttribute('aria-expanded', 'false');
      if (say && wlGet('sessionStorage', WB_SAID) !== '1') {
        wlSet('sessionStorage', WB_SAID, '1');
        wb.classList.add('is-say');
        wbHushT = setTimeout(wbHush, WB_HUSH);
      } else wb.classList.add('is-hush');
      wbLift();
      requestAnimationFrame(function () { requestAnimationFrame(function () { wb.classList.add('is-in'); wbLift(); }); });
      if (focus) wbOpen.focus({ preventScroll: true });
      window.addEventListener('scroll', wbOnScroll, { passive: true });
      window.addEventListener('resize', wbOnScroll);
      wbWatch = setInterval(function () { wb.classList.toggle('is-muted', wlBusy()); wbLift(); }, 400);
    }
    function wbHide() {
      if (!wbOn) return; wbOn = false;
      if (wb.contains(document.activeElement)) document.activeElement.blur();
      wb.classList.remove('is-in'); wb.hidden = true;
      clearInterval(wbWatch);
      window.removeEventListener('scroll', wbOnScroll); window.removeEventListener('resize', wbOnScroll);
    }
    function wbDismiss() { wlSet('localStorage', WL_KEY, 'dismissed:' + Date.now()); wbHide(); }
    function binShow(on) {
      if (!bin) return;
      if (on) { bin.hidden = false; requestAnimationFrame(function () { bin.classList.add('is-on'); }); }
      else { bin.classList.remove('is-on', 'is-hot'); setTimeout(function () { if (!wbDrag) bin.hidden = true; }, 220); }
    }
    function binNear(cx, cy) {                             // icon centre on, or within ~40 px of, the "x Remove" pill
      if (!bin || bin.hidden) return false;
      var r = bin.getBoundingClientRect();
      return cx > r.left - 40 && cx < r.right + 40 && cy > r.top - 40 && cy < r.bottom + 40;
    }
    // drag like a chat head: < 8 px of movement = a tap (the click below opens the card); a real drag never opens it
    function wbDown(e) {
      if (e.button > 0 || !wbOn) return;
      var r = wb.getBoundingClientRect();
      wbDrag = { id: e.pointerId, x: e.clientX, y: e.clientY, r: r, moved: false, dx: 0, dy: 0 };
      wbDragged = false;
      try { wbOpen.setPointerCapture(e.pointerId); } catch (x) {}
    }
    function wbMove(e) {
      var d = wbDrag; if (!d || e.pointerId !== d.id) return;
      d.dx = e.clientX - d.x; d.dy = e.clientY - d.y;
      if (!d.moved) {
        if (Math.abs(d.dx) < 8 && Math.abs(d.dy) < 8) return;
        d.moved = true; wbDragged = true;
        wbHush(); wb.classList.add('is-drag'); binShow(true);
      }
      e.preventDefault();
      wb.style.transform = 'translate(' + Math.round(d.dx) + 'px,' + Math.round(d.dy) + 'px) scale(1.06)';   // follows the finger 1:1, slightly lifted
      if (bin) bin.classList.toggle('is-hot', binNear(d.r.left + 24 + d.dx, d.r.top + 24 + d.dy));
    }
    function wbUp(e) {
      var d = wbDrag; if (!d || e.pointerId !== d.id) return;
      try { wbOpen.releasePointerCapture(e.pointerId); } catch (x) {}
      if (!d.moved) { wbDrag = null; return; }             // a tap: the click event opens the card
      var cx = d.r.left + 24 + d.dx, cy = d.r.top + 24 + d.dy;
      if (e.type === 'pointerup' && binNear(cx, cy)) {
        wbDrag = null; binShow(false); wb.classList.remove('is-drag'); wb.style.transform = '';
        wbDismiss(); return;                                // same 30-day quiet as before
      }
      binShow(false);
      var vh = window.innerHeight, vw = document.documentElement.clientWidth;
      if (e.type === 'pointerup') {
        var bot = vh - (d.r.bottom + d.dy);
        bot = Math.max(16, Math.min(bot, vh - 56));          // inside the viewport; a drop on the sticky buy bar is
                                                             // lifted just above it by wbLift while the bar shows
        wbPos = { s: cx < vw / 2 ? 'l' : 'r', b: Math.round(bot) };
        wlSet('sessionStorage', WB_POS, JSON.stringify(wbPos));
      }
      // jump the anchor to the snapped spot, hold the icon where it was dropped, then let the transform glide home
      wbDrag = null; wbLift();
      var r2 = wb.getBoundingClientRect();
      wb.style.transform = 'translate(' + Math.round(d.r.left + d.dx - r2.left) + 'px,' + Math.round(d.r.top + d.dy - r2.top) + 'px)';
      requestAnimationFrame(function () { requestAnimationFrame(function () {
        wb.classList.remove('is-drag'); wb.style.transform = ''; wbLift();
      }); });
    }
    if (wb) {
      wbOpen.addEventListener('pointerdown', wbDown);
      wbOpen.addEventListener('pointermove', wbMove);
      wbOpen.addEventListener('pointerup', wbUp);
      wbOpen.addEventListener('pointercancel', wbUp);
      wbOpen.addEventListener('dragstart', function (e) { e.preventDefault(); });
      wbOpen.addEventListener('click', function (e) {
        if (wbDragged) { wbDragged = false; e.preventDefault(); return; }   // the end of a drag is not a tap
        wbHush(); wbHide();
        show(); wbOpen.setAttribute('aria-expanded', 'true');
        el.focus({ preventScroll: true });
      });
      // keyboard only: an invisible "Dismiss" button that shows itself on :focus-visible
      wb.querySelector('.rl-wb__x').addEventListener('click', wbDismiss);
    }
    input.addEventListener('input', function () {
      if (!err.hidden) { err.hidden = true; input.removeAttribute('aria-invalid'); input.setAttribute('aria-describedby', 'rl-wl-legal'); }
    });
    form.addEventListener('submit', function (e) {
      var v = (input.value || '').trim();
      if (!/^[^\s@]+@[^\s@.]+(\.[^\s@.]+)*\.[^\s@.]{2,}$/.test(v)) {
        e.preventDefault();
        fail('Please enter a valid email address, like name@example.com.');
        input.focus();
        return;
      }
      input.value = v;
      wlSet('sessionStorage', WL_PEND, '1');
      if (bypass) { bypass = false; return; }                  // our own fallback: let Shopify's script post it
      if (e.defaultPrevented) return;                          // another script (captcha) owns this submit; we show the code on return
      if (!window.fetch || !window.FormData) return;          // old browser: the normal submit runs
      e.preventDefault();
      e.stopImmediatePropagation();                            // one submit only: no later handler re-posts it
      btn.disabled = true; btn.textContent = 'Sending…';
      fetch(form.action, { method: 'POST', body: new FormData(form), credentials: 'same-origin', headers: { Accept: 'text/html' } })
        .then(function (r) { return r.text().then(function (t) { return { r: r, t: t }; }); })
        .then(function (x) {
          var u = x.r.url || '';
          if (/\/challenge/.test(u) || !x.r.ok) return nativeSubmit();   // bot check: let the browser do it
          if (/customer_posted=true/.test(u) || /data-rl-wl-posted/.test(x.t)) return success(true);
          var m = /data-rl-wl-ferr[^>]*>([\s\S]*?)<\/span>/.exec(x.t);
          if (m) {
            var d = document.createElement('div'); d.innerHTML = m[1];
            return fail((d.textContent || '').trim() || 'That did not go through. Please check the address and try again.');
          }
          nativeSubmit();
        })
        .catch(nativeSubmit);
    });
    if (copy) copy.addEventListener('click', function () {
      var code = (el.querySelector('.rl-wl__code') || {}).textContent || 'ROOMLIFT10';
      function ok() { copy.textContent = 'Copied'; setTimeout(function () { copy.textContent = 'Copy code'; }, 3000); }
      function legacy() {
        try {
          var r = document.createRange(); r.selectNodeContents(el.querySelector('.rl-wl__code'));
          var s = window.getSelection(); s.removeAllRanges(); s.addRange(r);
          if (document.execCommand('copy')) ok();
        } catch (e) {}
      }
      try { navigator.clipboard.writeText(code).then(ok, legacy); } catch (e) { legacy(); }
    });

    // back from a normal (non-fetch) submit: runs BEFORE the preview hook and ignores 'subscribed'
    var pend = wlGet('sessionStorage', WL_PEND), ours = location.hash === '#rl-wl-form';
    function cleanUrl() {
      try {
        var q = location.search.replace(/([?&])customer_posted=true(&|$)/, function (m, a, b) { return b ? a : ''; }).replace(/[?&]$/, '');
        history.replaceState(history.state, '', location.pathname + q);
      } catch (e) {}
    }
    if (/[?&]customer_posted=true/.test(location.search) && el.querySelector('[data-rl-wl-posted]') &&
        (pend === '1' || pend === 'done' || ours)) {
      success(false); cleanUrl(); return;
    }
    if (el.querySelector('[data-rl-wl-ferr]') && (pend === '1' || ours)) {
      var f = el.querySelector('[data-rl-wl-ferr]');
      show(); fail((f.textContent || '').trim() || 'That did not go through. Please check the address and try again.');
      wlSet('sessionStorage', WL_PEND, null);
      return;
    }


    if (wlQuiet() || wlBadPath() || wlCreator()) return;
    var said = wlGet('sessionStorage', WB_SAID) === '1';
    var wait = Math.max(0, (wb && said ? 1500 : WL_DELAY) - (window.performance && performance.now ? performance.now() : 0));
    setTimeout(function tryShow() {
      if (wlQuiet() || wlCreator()) return;
      if (wlBusy()) { setTimeout(tryShow, 500); return; }
      if (wb) wbShow(true); else show();
    }, wait);
  }

  /* PDP LAYER 2 (design1, 2026-10-03, CSS section 36): (1) a "3 / 8" counter in the corner of the
     product photo frame, kept in step with Horizon's slideshow:select event; (2) the "See the dimension
     drawing" button in the Will-it-fit card selects the LAST gallery photo (via its thumbnail, so
     Horizon does the work) and brings the gallery into view. Both are delegated / re-resolved on every
     call, so a Horizon morph of the gallery is picked up on the next event; listeners bind once. */
  var d1Bound = false;
  function d1Gallery() {
    var sc = document.querySelector('.product-information__media slideshow-component');
    if (!sc) return null;
    var slides = (sc.slides && sc.slides.length) ? sc.slides : sc.querySelectorAll('slideshow-slide:not([hidden])');
    return { sc: sc, n: slides.length };
  }
  function d1Count(idx) {
    var g = d1Gallery(); if (!g) return;
    var box = g.sc.querySelector('slideshow-container'); if (!box) return;
    var el = box.querySelector(':scope > .rl-gcount');
    if (g.n < 2) { if (el) el.remove(); return; }
    if (!el) { el = document.createElement('p'); el.className = 'rl-gcount'; el.setAttribute('aria-hidden', 'true'); box.appendChild(el); }
    var i = typeof idx === 'number' ? idx : (typeof g.sc.current === 'number' ? g.sc.current : 0);
    var txt = (Math.min(Math.max(i, 0), g.n - 1) + 1) + ' / ' + g.n;
    if (el.textContent !== txt) el.textContent = txt;
  }
  function design1() {
    d1Count();
    if (d1Bound) return;
    d1Bound = true;
    document.addEventListener('slideshow:select', function (e) {
      var g = d1Gallery(); if (!g || !g.sc.contains(e.target)) return;
      d1Count(e.detail && typeof e.detail.index === 'number' ? e.detail.index : undefined);
    });
    document.addEventListener('variant:update', function () { setTimeout(d1Count, 300); });
    document.addEventListener('click', function (e) {
      var b = e.target.closest && e.target.closest('[data-rl-fit-drawing]');
      if (!b) return;
      e.preventDefault();
      var thumbs = document.querySelectorAll('.product-information__media slideshow-controls .slideshow-controls__thumbnail');
      var last = thumbs[thumbs.length - 1];
      var g = d1Gallery();
      if (last) last.click();
      else if (g && typeof g.sc.select === 'function') g.sc.select(g.n - 1);
      var media = document.querySelector('.product-information__media');
      if (!media) return;
      var hd = document.querySelector('#header-component, header-component'), off = 8;
      if (hd) { var hr = hd.getBoundingClientRect(); if (hr.bottom > 0 && hr.top <= 0 + 1) off += hr.height; }
      window.scrollTo({ top: Math.max(0, media.getBoundingClientRect().top + window.scrollY - off), behavior: 'smooth' });
    });
  }

  /* fix3 2026-10-05: M1 - the gallery follows the chosen colour / size. On a product with 2+ versions
     every version's photos sat in one long list (Wilbury Black showed natural-wood thumbnails; a
     3-size rug read "1 / 32"). Photo file names start with the version's number, which is also its
     SKU (Wilbury Natural 568412_*, Black 568413_*). After load and after every variant change
     (Horizon swaps in a fresh, complete gallery on variant:update), the photos of the OTHER versions
     are taken out of the slides, the thumbnails, the dots/counter, the grid and the zoom view; the
     chosen version's photos and anything that is no version's (shared drawings) stay. The first
     slide (the version's own lead photo, put first by Horizon) always stays. Nothing changes when
     the product has one version, when no photo carries the chosen version's number, or when the
     lists do not line up. Index-bearing attributes ("/select/3", ".../open/3") are renumbered so
     Horizon's slideshow, thumbnails and zoom keep pointing at the right photo. Idempotent. */
  var rlvgBound = false;
  function rlvgSkus() {
    var out = [];
    document.querySelectorAll('script[type="application/ld+json"]').forEach(function (s) {
      var t = s.textContent || '';
      if (t.indexOf('hasVariant') === -1) return;
      try {
        [].concat(JSON.parse(t)).forEach(function (grp) {
          ((grp && grp.hasVariant) || []).forEach(function (v) { if (v && v.sku) out.push(String(v.sku).trim()); });
        });
      } catch (e) { /* unreadable JSON-LD: leave the gallery alone */ }
    });
    return out;
  }
  function rlvgSelected(scope) {
    var s = scope.querySelector('variant-picker[data-template-product-match="true"] script[type="application/json"]') ||
            scope.querySelector('variant-picker script[type="application/json"]');
    if (!s) return null;
    try { var v = JSON.parse(s.textContent); return v && v.sku ? String(v.sku).trim() : null; } catch (e) { return null; }
  }
  function rlvgStem(el) {
    // fix13 (2026-10-08): a video slide's files have hashed names, so its version number is read from the
    // leading 6-digit SKU of its alt / aria-label text ("694869 · The Lenham …", "Load video: 694869 · …").
    if (el && el.querySelector('deferred-media, video-media, video')) {
      var txt = [];
      Array.prototype.forEach.call(el.querySelectorAll('[alt], [aria-label]'), function (x) {
        txt.push(x.getAttribute('alt') || '', x.getAttribute('aria-label') || '');
      });
      for (var t = 0; t < txt.length; t++) {
        var vm = txt[t].replace(/^\s*Load video:\s*/i, '').match(/^\s*(\d{6})\b/);
        if (vm) return vm[1];
      }
      return '';
    }
    var img = el && el.querySelector('img');
    if (!img) return '';
    var u = img.getAttribute('src') || img.currentSrc || (img.getAttribute('srcset') || '').split(' ')[0] || '';
    var m = u.split('?')[0].match(/\/([^\/]+)$/);
    return m ? m[1].split('_')[0] : '';
  }
  function rlvgName(el) {
    // the slide's media file name without extension, lower case ('' for a video)
    var img = el && el.querySelector('img');
    if (!img || el.querySelector('deferred-media, video-media, video')) return '';
    var u = img.getAttribute('src') || img.currentSrc || (img.getAttribute('srcset') || '').split(' ')[0] || '';
    var m = u.split('?')[0].match(/\/([^\/]+)$/);
    return m ? m[1].replace(/\.[a-z0-9]+$/i, '').toLowerCase() : '';
  }
  function rlvgRenumber(el, n, total) {
    var nodes = [el].concat(Array.prototype.slice.call(el.querySelectorAll('[on\\:click], [on\\:pointerenter]')));
    nodes.forEach(function (x) {
      ['on:click', 'on:pointerenter'].forEach(function (a) {
        var v = x.getAttribute(a);
        if (v && /\/\d+$/.test(v)) x.setAttribute(a, v.replace(/\/\d+$/, '/' + n));
      });
      var lab = x.getAttribute('aria-label');
      if (lab && /^\D*\d+\D+\d+\D*$/.test(lab) && x.matches('.slideshow-control')) {
        var k = 0;
        x.setAttribute('aria-label', lab.replace(/\d+/g, function () { k++; return k === 1 ? String(n + 1) : String(total); }));
      }
    });
  }
  function rlVariantGallery() {
    var g = document.querySelector('.product-information__media media-gallery');
    if (!g) return;
    var skus = rlvgSkus();
    // gallery-all 2026-10-10: photos listed in roomlift.gallery_hide (text panels, the hero's source cut-out,
    // the supplier drawing the clean copy replaces) are taken out the same way, on every product.
    var hide = [];
    try { var hs = document.querySelector('script[data-rl-ghide]'); if (hs) hide = JSON.parse(hs.textContent) || []; } catch (e) { hide = []; }
    var sel = rlvgSelected(g.closest('.shopify-section') || document);
    var byVersion = skus.length >= 2 && sel && skus.indexOf(sel) !== -1;
    if (!byVersion && !hide.length) return;                            // one version, nothing hidden
    var slides = Array.prototype.slice.call(g.querySelectorAll('slideshow-slides > slideshow-slide'));
    var base = slides.length ? slides : Array.prototype.slice.call(g.querySelectorAll('.media-gallery__grid > li'));
    if (base.length < 2) return;
    var stems = base.map(rlvgStem);
    if (byVersion && stems.indexOf(sel) === -1) byVersion = false;     // no photo of this version: show them all
    var names = base.map(rlvgName);
    var keep = stems.map(function (s, i) {
      if (hide.indexOf(names[i]) !== -1 && i !== 0) return false;
      return !byVersion || i === 0 || s === sel || skus.indexOf(s) === -1;
    });
    var total = keep.filter(Boolean).length;
    if (total === base.length) return;                                 // already filtered (or nothing to hide)
    function prune(list, isDot) {
      list = Array.prototype.slice.call(list);
      if (list.length !== keep.length) return;                         // does not line up with the slides: leave it
      var n = 0;
      list.forEach(function (el, i) {
        if (!keep[i]) { el.remove(); return; }
        rlvgRenumber(el, n, total);
        if (isDot) { var bt = el.querySelector('button') || el; if (/^\s*\d+\s*$/.test(bt.textContent)) bt.textContent = String(n + 1); }
        n++;
      });
    }
    prune(slides);
    prune(g.querySelectorAll('.media-gallery__grid > li'));
    g.querySelectorAll('slideshow-controls').forEach(function (c) {
      prune(c.querySelectorAll('.slideshow-controls__thumbnail'));
      prune(c.querySelectorAll('.slideshow-controls__dots > li'), true);
      var cnt = c.querySelector('.slideshow-controls__counter');
      if (cnt && cnt.lastChild && cnt.lastChild.nodeType === 3) cnt.lastChild.textContent = String(total);
    });
    var zt = g.querySelector('.dialog-thumbnails-list');
    if (zt) prune(zt.querySelectorAll(':scope > .dialog-thumbnails-list__thumbnail'));
    prune(g.querySelectorAll('.dialog-zoomed-gallery > li'));
    // Horizon's components cache their refs (slides, thumbnails, zoom media) and do not notice a removed
    // node; updatedCallback() is the Component hook that re-reads them (the same one its morph uses).
    [g].concat(Array.prototype.slice.call(g.querySelectorAll('slideshow-component, zoom-dialog'))).forEach(function (c) {
      if (typeof c.updatedCallback === 'function') { try { c.updatedCallback(); } catch (e) { /* not ready: refs are read on upgrade */ } }
    });
    g.setAttribute('data-rl-version-photos', sel);
    setTimeout(d1Count, 0);
  }
  function variantGallery() {
    rlVariantGallery();
    if (rlvgBound) return;
    rlvgBound = true;
    document.addEventListener('variant:update', function () {
      rlVariantGallery();                                              // the fresh gallery is already in place
      setTimeout(rlVariantGallery, 150);
      setTimeout(rlVariantGallery, 600);
    });
  }

  function start() {
    variantGallery();
    cartKeep();
    menuAlign();
    searchDivider();
    stickyCheckout();
    gridEnd();
    stickyAtc();
    filterSpecTables();
    dealTimer();
    design1();
    welcome();
    prime();
    setTimeout(revealAll, 3000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.addEventListener('load', function () { setTimeout(revealAll, 1500); });
  document.addEventListener('shopify:section:load', start);

  // GALLERY THUMBNAILS: Horizon marks them loading="lazy", but the browser
  // never fires the lazy trigger for them (user-visible as blank grey squares
  // under the product photo on mobile). A 44-90px thumbnail costs a few KB —
  // eager is correct. Covers the strip and the zoom dialog; re-runs after
  // Horizon section re-renders.
  function eagerThumbs() {
    // NOTE: slideshow-controls is a CUSTOM ELEMENT (tag), not a class —
    // '.slideshow-controls' matches nothing. Select by tag and by the
    // __thumbnails class both, and re-run late for slow hydration.
    document.querySelectorAll(
      'slideshow-controls img[loading="lazy"], ' +
      '.slideshow-controls__thumbnails img[loading="lazy"], ' +
      '.dialog-thumbnails img[loading="lazy"]'
    ).forEach(function (img) { img.loading = 'eager'; });
  }
  eagerThumbs();
  window.addEventListener('load', eagerThumbs);
  setTimeout(eagerThumbs, 1500);
  setTimeout(eagerThumbs, 4000);
  document.addEventListener('shopify:section:load', eagerThumbs);

  /* FIX11-CHAIN DELIVERY WINDOW (2026-10-07, owner): every [data-rl-arrive] (the promise line under the price and
     the "Nothing to lose" block, snippets/rl-chain.liquid + rl_promise) carries the static fallback
     "5-10 business days"; this swaps it for the dated window, data-rl-arrive="Arrives {d}" ->
     "Arrives Oct 14-21" (or "Arrives Oct 30 - Nov 6"). Window = today + 5 and today + 10 BUSINESS days in the
     shopper's local time: Saturdays, Sundays and the six days the parcel carriers do not deliver (New Year's Day,
     Memorial Day, Independence Day, Labor Day, Thanksgiving, Christmas; a Saturday holiday moves to the Friday,
     a Sunday one to the Monday) are skipped. Liquid leaves the span out on freight (LTL) pieces and on a sold-out
     version. Re-runs after a colour switch / section reload (Horizon re-renders the blocks). */
  function rlArrive() {
    var els = document.querySelectorAll('[data-rl-arrive]:not([data-rl-arrive-done])');
    if (!els.length) return;
    var MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    var hol = {};
    function key(d) { return d.getFullYear() + '-' + d.getMonth() + '-' + d.getDate(); }
    function nth(y, m, wd, n) { var d = new Date(y, m, 1); d.setDate(1 + (wd - d.getDay() + 7) % 7 + (n - 1) * 7); return d; }
    function last(y, m, wd) { var d = new Date(y, m + 1, 0); d.setDate(d.getDate() - (d.getDay() - wd + 7) % 7); return d; }
    function fixed(y, m, day) { var d = new Date(y, m, day), w = d.getDay(); if (w === 6) d.setDate(day - 1); if (w === 0) d.setDate(day + 1); return d; }
    function addYear(y) {
      [fixed(y, 0, 1), last(y, 4, 1), fixed(y, 6, 4), nth(y, 8, 1, 1), nth(y, 10, 4, 4), fixed(y, 11, 25)]
        .forEach(function (d) { hol[key(d)] = 1; });
    }
    var now = new Date(), d0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    addYear(d0.getFullYear()); addYear(d0.getFullYear() + 1);
    function plus(n) {
      var d = new Date(d0.getTime()), c = 0;
      while (c < n) { d.setDate(d.getDate() + 1); var w = d.getDay(); if (w !== 0 && w !== 6 && !hol[key(d)]) c++; }
      return d;
    }
    var a = plus(5), b = plus(10);
    var txt = a.getMonth() === b.getMonth()
      ? MON[a.getMonth()] + ' ' + a.getDate() + '–' + b.getDate()
      : MON[a.getMonth()] + ' ' + a.getDate() + ' – ' + MON[b.getMonth()] + ' ' + b.getDate();
    els.forEach(function (el) {
      el.textContent = (el.getAttribute('data-rl-arrive') || 'Arrives {d}').replace('{d}', txt);
      el.setAttribute('data-rl-arrive-done', '');
    });
  }
  window.rlArrive = rlArrive;   // chain-B3: list cards loaded by infinite scroll get their dates too
  rlArrive();
  document.addEventListener('DOMContentLoaded', rlArrive);
  document.addEventListener('shopify:section:load', rlArrive);

  /* FIX11-CHAIN COLOUR SWITCH: Horizon swaps the gallery, price and buttons on a colour change but not our
     custom-liquid blocks. Its variant:update event carries the new version's rendered page (detail.data.html),
     so the chain parts (FIT / ROOM / PROOF / SAFE), the price-row line and the promise line are replaced from it:
     the picker products' per-colour photo sets follow the colour. Only nodes of the same product (data-rl-pid)
     are swapped. */
  function rlChainSwap(e) {
    var h = e && e.detail && e.detail.data && e.detail.data.html;
    if (!h || !h.querySelector) return;
    var sels = ['fit', 'room', 'proof', 'safe'].map(function (k) { return '[data-rl-chain="' + k + '"], [data-rl-chain-off="' + k + '"]'; });
    sels.push('.rl-psold, .rl-psold-off', '.rl-promise[data-rl-pid]', '.rl-deal[data-rl-pid], .rl-deal-off[data-rl-pid]');   // fix12: the Was/timer row follows the variant
    sels.forEach(function (sel) {
      var cur = document.querySelector(sel), nu = h.querySelector(sel);
      if (!cur || !nu || cur.getAttribute('data-rl-pid') !== nu.getAttribute('data-rl-pid')) return;
      cur.replaceWith(document.importNode(nu, true));
    });
    rlArrive();
  }
  document.addEventListener('variant:update', function (e) {
    try { rlChainSwap(e); } catch (err) {}
    setTimeout(rlArrive, 0); setTimeout(rlArrive, 400); setTimeout(rlArrive, 1500);
  });

})();

/* ENGAGED PRODUCT VIEW (2026-10-09, owner: retargeting audience from the behaviour Clarity shows).
   On a product page, the FIRST of these marks the visit as engaged (once per page view):
     scroll  - scrolled past the first screen
     photo   - swiped / tapped to another gallery photo (or a gallery dot / thumbnail)
     click   - any other tap on the product area (colour picker, sections, Add to cart, links)
   It then (a) publishes Shopify customer event "rl_engaged" (a Shopify custom pixel forwards it to the
   Meta pixel as custom event RLEngagedView -> the retargeting audience), and (b) tags the Clarity session
   (custom tag rl_engaged = scroll|photo|click + event rl_engaged) so the counts can be compared. */
(function () {
  if (!/\/products\//.test(location.pathname)) return;
  var done = false;
  function fire(how) {
    if (done) return; done = true;
    var m = (window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product) || {};
    var v = (m.variants || [])[0] || {};
    var sel = document.querySelector('form[action*="/cart/add"] [name="id"]');
    if (sel && m.variants) { m.variants.forEach(function (x) { if (String(x.id) === String(sel.value)) v = x; }); }
    var data = { how: how, product_id: String(m.id || ''), sku: String(v.sku || ''),
                 title: document.title.split(' \u2013 ')[0], value: v.price ? v.price / 100 : null, currency: 'USD' };
    try { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish('rl_engaged', data); } catch (e) {}
    try { if (window.clarity) { clarity('set', 'rl_engaged', how); clarity('event', 'rl_engaged'); } } catch (e) {}
    window.rlEngaged = data;
  }
  window.addEventListener('scroll', function () {
    if (window.scrollY > window.innerHeight) fire('scroll');
  }, { passive: true });
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (t.closest('.product-information__media, media-gallery, slideshow-component')) { fire('photo'); return; }
    if (t.closest('main')) fire('click');
  }, true);
  // swipes on the phone gallery do not click: watch the selected slide change
  var tries = 0, iv = setInterval(function () {
    var g = document.querySelector('.product-information__media slideshow-component, .product-information__media media-gallery');
    if (g || ++tries > 20) clearInterval(iv);
    if (!g) return;
    new MutationObserver(function (ms) {
      for (var i = 0; i < ms.length; i++) {
        if (ms[i].attributeName === 'aria-selected' && ms[i].target.getAttribute('aria-selected') === 'true') { fire('photo'); return; }
      }
    }).observe(g, { attributes: true, subtree: true, attributeFilter: ['aria-selected'] });
  }, 500);
})();

/* ==========================================================================
   CHAIN-B (2026-10-10, whole-store chain plan §2.3) - collection pages only, self-contained.
   - one "every order" card inside the grid (after row 2 on a phone, row 1 from 750 px); placed again after
     Horizon re-renders the grid (filters, sort, infinite scroll) and on a width change
   - a question line under the grid (after the "Showing / That is all" line)
   - sort list: "Most relevant" and "Best selling" removed here (search keeps them)
   - phone: the intro folds to two lines behind a "More" button
   ========================================================================== */
(function () {
  if (location.pathname.indexOf('/collections/') !== 0) return;
  var ICON = {
    ship: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" focusable="false"><path d="M2.5 5.5h11v11h-11zM13.5 9.5h4.25l3.25 3.75v3.25h-7.5"/><circle cx="6.5" cy="17.75" r="1.75"/><circle cx="17" cy="17.75" r="1.75"/></svg>',
    ret: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" focusable="false"><path d="M8.5 4.5L4.5 8.5l4 4"/><path d="M4.5 8.5h10a5.5 5.5 0 0 1 0 11H10"/></svg>',
    box: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" focusable="false"><path d="M3 7.25L12 3l9 4.25v9.5L12 21l-9-4.25z"/><path d="M3 7.25L12 11.5l9-4.25M12 11.5V21"/></svg>',
    who: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" focusable="false"><circle cx="12" cy="8" r="3.75"/><path d="M4.5 20.5c.9-3.9 3.9-6 7.5-6s6.6 2.1 7.5 6"/></svg>'
  };
  var PROMISE = '<p class="rl-gridpromise__h">Every order, the same terms</p><ul class="rl-gridpromise__l">' +
    '<li class="rl-gridpromise__i">' + ICON.ship + '<span>Free shipping to the 48 states + DC. Most orders arrive in 5&ndash;10 business days.</span></li>' +
    '<li class="rl-gridpromise__i">' + ICON.ret + '<span><a href="/policies/refund-policy">4-week returns</a>, no restocking fee.</span></li>' +
    '<li class="rl-gridpromise__i">' + ICON.box + '<span>Damaged in transit? Tell us within 36 hours.</span></li>' +
    '<li class="rl-gridpromise__i">' + ICON.who + '<span>A real person answers. <a href="mailto:support@getroomlift.com">Email us</a></span></li></ul>';

  function cols(grid) {
    var t = getComputedStyle(grid).gridTemplateColumns || '';
    var n = t.split(' ').filter(function (x) { return x && x !== '0px'; }).length;
    return n || 2;
  }
  function placePromise() {
    var grid = document.querySelector('results-list .product-grid');
    if (!grid) return;
    var items = grid.querySelectorAll(':scope > .product-grid__item');
    var c = cols(grid);
    var after = c <= 2 ? 4 : c;               // phone: after row 2; tablet/desktop: after row 1
    var card = grid.querySelector(':scope > .rl-gridpromise');
    if (items.length <= after) { if (card) card.remove(); return; }
    var anchor = items[after - 1];
    if (card && card.previousElementSibling === anchor) return;
    if (!card) {
      card = document.createElement('li');
      card.className = 'rl-gridpromise';
      card.setAttribute('aria-label', 'Delivery, returns and help');
      card.innerHTML = PROMISE;
    }
    anchor.insertAdjacentElement('afterend', card);
  }
  function ask() {
    var host = document.querySelector('results-list');
    if (!host || host.querySelector('.rl-grid-ask')) return;
    var p = document.createElement('p');
    p.className = 'rl-grid-ask';
    p.innerHTML = 'Questions about size or material? <a href="mailto:support@getroomlift.com">support@getroomlift.com</a> &mdash; a person answers within one business day.';
    var end = host.querySelector('.rl-grid-end') || host.querySelector('.product-grid');
    if (end) end.insertAdjacentElement('afterend', p);
  }
  function sortTrim() {
    document.querySelectorAll('option[value="most-relevant"], option[value="best-selling"]').forEach(function (o) { if (!o.selected) o.remove(); });
    document.querySelectorAll('input[name="sort_by"][value="most-relevant"], input[name="sort_by"][value="best-selling"]').forEach(function (i) {
      if (i.checked) return;
      var row = i.closest('li') || i.parentElement;
      if (row) row.remove();
    });
  }
  function fold() {
    var d = document.querySelector('.rl-collhero__desc');
    if (!d || d.getAttribute('data-rl-fold') || window.innerWidth >= 750) return;
    d.setAttribute('data-rl-fold', '1');
    d.classList.add('rl-fold');
    if (d.scrollHeight <= d.clientHeight + 4) { d.classList.remove('rl-fold'); return; }
    var b = document.createElement('button');
    b.type = 'button'; b.className = 'rl-collhero__more'; b.textContent = 'More'; b.setAttribute('aria-expanded', 'false');
    b.addEventListener('click', function () {
      var open = d.classList.toggle('rl-fold') === false;
      b.textContent = open ? 'Less' : 'More';
      b.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    d.insertAdjacentElement('afterend', b);
  }
  function all() { try { placePromise(); ask(); sortTrim(); fold(); } catch (e) {} }
  var q = false;
  function soon() { if (q) return; q = true; setTimeout(function () { q = false; all(); }, 150); }
  function boot() {
    all();
    var host = document.querySelector('main') || document.body;
    new MutationObserver(function (ms) {
      for (var i = 0; i < ms.length; i++) {
        var t = ms[i].target;
        if (t.closest && t.closest('.rl-gridpromise, .rl-grid-ask')) continue;
        soon(); return;
      }
    }).observe(host, { childList: true, subtree: true });
    var w = window.innerWidth;
    window.addEventListener('resize', function () { if (window.innerWidth !== w) { w = window.innerWidth; soon(); } });
    document.addEventListener('shopify:section:load', soon);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();

/* ==========================================================================
   CHAIN-B2 (2026-10-10, owner: "collection page don't lure the buyer") - "In a room" tiles inside the collection
   grid. Data: #rl-coll-rooms (hero section, Liquid). Only pieces that are in the grid right now are featured, each
   once, always a piece further DOWN the grid than the tile (it pulls the eye on). Phone: after cards 10 and 22;
   3-4 columns: after rows 3 and 6. Re-placed after Horizon re-renders the grid; never touches .product-grid__item.
   ========================================================================== */
(function () {
  if (location.pathname.indexOf('/collections/') !== 0) return;
  var data = null;
  function load() {
    if (data) return data;
    var el = document.getElementById('rl-coll-rooms');
    try { data = el ? JSON.parse(el.textContent) : []; } catch (e) { data = []; }
    return data;
  }
  function esc(t) { var d = document.createElement('div'); d.textContent = t == null ? '' : String(t); return d.innerHTML; }
  function cols(grid) {
    var n = (getComputedStyle(grid).gridTemplateColumns || '').split(' ').filter(function (x) { return x && x !== '0px'; }).length;
    return n || 2;
  }
  function tile(r) {
    var li = document.createElement('li');
    li.className = 'rl-gridroom';
    li.setAttribute('data-rl-room', r.id);
    li.innerHTML = '<a class="rl-gridroom__a" href="' + esc(r.u) + '">' +
      '<img class="rl-gridroom__img" loading="lazy" src="' + esc(r.i6) + '" srcset="' + esc(r.i6) + ' 600w, ' + esc(r.i) + ' 900w" sizes="(min-width: 750px) 60vw, 100vw" alt="' + esc(r.t + ' - ' + r.c) + '">' +
      '<span class="rl-gridroom__txt"><span class="rl-gridroom__k">In a room</span><span class="rl-gridroom__n">' + esc(r.t) + '</span>' +
      (r.c ? '<span class="rl-gridroom__c">' + esc(r.c) + '</span>' : '') +
      '<span class="rl-gridroom__p">' + esc(r.p) + '</span><span class="rl-gridroom__go">See it &rarr;</span></span></a>';
    return li;
  }
  function place() {
    var grid = document.querySelector('results-list .product-grid');
    var rooms = load();
    if (!grid || !rooms.length) return;
    var items = Array.prototype.slice.call(grid.querySelectorAll(':scope > .product-grid__item'));
    var c = cols(grid);
    var spots = c <= 2 ? [10, 22] : [c * 3, c * 6];
    var byId = {}; rooms.forEach(function (r) { byId[String(r.id)] = r; });
    var used = {};
    var want = [];
    spots.forEach(function (after) {
      if (items.length <= after) return;
      var pick = null;
      for (var k = after; k < items.length && !pick; k++) {      // a piece further down the grid
        var id = items[k].getAttribute('data-product-id');
        if (byId[id] && !used[id]) pick = byId[id];
      }
      for (var k2 = 0; k2 < after && !pick; k2++) {               // else any piece in the grid
        var id2 = items[k2].getAttribute('data-product-id');
        if (byId[id2] && !used[id2]) pick = byId[id2];
      }
      if (!pick) return;
      used[String(pick.id)] = 1;
      want.push({ after: after, r: pick });
    });
    var have = Array.prototype.slice.call(grid.querySelectorAll(':scope > .rl-gridroom'));
    var ok = have.length === want.length && want.every(function (w, i) {
      return have[i].getAttribute('data-rl-room') === String(w.r.id) && have[i].previousElementSibling === items[w.after - 1];
    });
    if (ok) return;
    have.forEach(function (h) { h.remove(); });
    want.forEach(function (w) { items[w.after - 1].insertAdjacentElement('afterend', tile(w.r)); });
  }
  var q = false;
  function soon() { if (q) return; q = true; setTimeout(function () { q = false; try { place(); } catch (e) {} }, 180); }
  function boot() {
    soon();
    var host = document.querySelector('main') || document.body;
    new MutationObserver(function (ms) {
      for (var i = 0; i < ms.length; i++) {
        var t = ms[i].target;
        if (t.closest && t.closest('.rl-gridroom, .rl-gridpromise, .rl-grid-ask')) continue;
        soon(); return;
      }
    }).observe(host, { childList: true, subtree: true });
    var w = window.innerWidth;
    window.addEventListener('resize', function () { if (window.innerWidth !== w) { w = window.innerWidth; soon(); } });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();

/* ==========================================================================
   CHAIN-B3 (2026-10-10, owner) - phone List / Grid switch on collection pages. List is the default (CSS 54);
   html.rl-view-grid = grid (was rl-grid until 2026-10-11: it clashed with the .rl-grid layout utility and turned <html> into a 2-column grid on phones), remembered in localStorage 'rl-view'. The switch sits after the phone count in the
   "Filter and sort" row and is put back after Horizon re-renders the filters. A name that needs three lines
   shortens the description to one (.rl-lc--long) so the card stays the photo's height.
   ========================================================================== */
(function () {
  if (location.pathname.indexOf('/collections/') !== 0) return;
  var LIST = '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" focusable="false"><rect x="1.5" y="2" width="5" height="5"/><path d="M9 3.5h7.5M9 6h5"/><rect x="1.5" y="11" width="5" height="5"/><path d="M9 12.5h7.5M9 15h5"/></svg>';
  var GRID = '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" focusable="false"><rect x="1.5" y="1.5" width="6.25" height="6.25"/><rect x="10.25" y="1.5" width="6.25" height="6.25"/><rect x="1.5" y="10.25" width="6.25" height="6.25"/><rect x="10.25" y="10.25" width="6.25" height="6.25"/></svg>';
  var root = document.documentElement;
  function isGrid() { return root.classList.contains('rl-view-grid'); }
  function set(grid) {
    root.classList.toggle('rl-view-grid', grid);
    try { localStorage.setItem('rl-view', grid ? 'grid' : 'list'); } catch (e) {}
    paint();
    setTimeout(fit, 50);
  }
  function paint() {
    document.querySelectorAll('.rl-viewsw').forEach(function (sw) {
      sw.querySelector('[data-v="list"]').setAttribute('aria-pressed', isGrid() ? 'false' : 'true');
      sw.querySelector('[data-v="grid"]').setAttribute('aria-pressed', isGrid() ? 'true' : 'false');
    });
  }
  function mount() {
    document.querySelectorAll('.facets-toggle .rl-count-mobile').forEach(function (cnt) {
      if (cnt.parentElement.querySelector('.rl-viewsw')) return;
      var sw = document.createElement('div');
      sw.className = 'rl-viewsw'; sw.setAttribute('role', 'group'); sw.setAttribute('aria-label', 'View');
      sw.innerHTML = '<button type="button" data-v="list" aria-label="List view">' + LIST + '</button><button type="button" data-v="grid" aria-label="Grid view">' + GRID + '</button>';
      sw.addEventListener('click', function (e) {
        var b = e.target.closest('button[data-v]'); if (!b) return;
        set(b.getAttribute('data-v') === 'grid');
      });
      cnt.insertAdjacentElement('afterend', sw);
    });
    paint();
  }
  function fit() {
    if (isGrid() || window.innerWidth >= 750) return;
    document.querySelectorAll('.rl-lc:not([data-rl-fit])').forEach(function (lc) {
      var nm = lc.querySelector('.rl-lc__nm'); if (!nm || !nm.offsetHeight) return;
      var lh = parseFloat(getComputedStyle(nm).lineHeight) || 19;
      lc.classList.toggle('rl-lc--long', nm.scrollHeight > lh * 2 + 2);
      var tag = lc.querySelector('.rl-lc__tag');   // a size that does not fit: 2-decimal inches rounded to 1 (19.69 -> 19.7)
      if (tag && tag.scrollWidth > tag.clientWidth) tag.textContent = tag.textContent.replace(/\d+\.\d{2,}/g, function (n) { return String(Math.round(parseFloat(n) * 10) / 10); });
      lc.setAttribute('data-rl-fit', '');
    });
  }
  function all() { try { mount(); fit(); if (window.rlArrive) window.rlArrive(); } catch (e) {} }
  var q = false;
  function soon() { if (q) return; q = true; setTimeout(function () { q = false; all(); }, 150); }
  function boot() {
    all();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () {
      document.querySelectorAll('.rl-lc[data-rl-fit]').forEach(function (lc) { lc.removeAttribute('data-rl-fit'); });
      fit();
    });
    new MutationObserver(function (ms) {
      for (var i = 0; i < ms.length; i++) {
        var t = ms[i].target;
        if (t.closest && t.closest('.rl-viewsw, .rl-gridroom, .rl-gridpromise')) continue;
        soon(); return;
      }
    }).observe(document.querySelector('main') || document.body, { childList: true, subtree: true });
    window.addEventListener('resize', soon);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();

/* ==========================================================================
   CHAIN-C3 (2026-10-10) - phone menu: the category rail switches the right-hand pane. Delegated, so it survives
   Horizon re-rendering the drawer. Up/Down arrows move between categories (tablist pattern).
   ========================================================================== */
(function () {
  function pick(tab, focus) {
    var nav = tab.closest('.rl-mnav'); if (!nav) return;
    nav.querySelectorAll('[data-rl-mtab]').forEach(function (t) {
      var on = t === tab;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
      var p = nav.querySelector('#' + t.getAttribute('aria-controls'));
      if (p) p.hidden = !on;
    });
    if (focus) tab.focus();
  }
  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[data-rl-mtab]');
    if (t) { e.preventDefault(); pick(t, false); }
  });
  document.addEventListener('keydown', function (e) {
    var t = e.target.closest && e.target.closest('[data-rl-mtab]');
    if (!t || (e.key !== 'ArrowDown' && e.key !== 'ArrowUp')) return;
    var all = Array.prototype.slice.call(t.closest('.rl-mnav').querySelectorAll('[data-rl-mtab]'));
    var i = all.indexOf(t) + (e.key === 'ArrowDown' ? 1 : -1);
    if (all[i]) { e.preventDefault(); pick(all[i], true); }
  });
})();

/* ==========================================================================
   CHAIN-D (2026-10-10) - footer link groups start folded on phones (they are open in the HTML, so no-JS shows all).
   ========================================================================== */
(function () {
  function fold() {
    if (window.innerWidth >= 750) return;
    document.querySelectorAll('details.rl-foot__grp[open]:not([data-rl-folded])').forEach(function (d) { d.removeAttribute('open'); d.setAttribute('data-rl-folded', ''); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fold); else fold();
  document.addEventListener('shopify:section:load', fold);
})();

/* 58b · a hairline under the pinned brand row once the menu has scrolled */
(function () {
  document.addEventListener('scroll', function (e) {
    var d = e.target; if (!d || !d.classList || !d.classList.contains('menu-drawer') || !d.querySelector('.rl-mnav')) return;
    d.classList.toggle('rl-mnav-scrolled', d.scrollTop > 8);
  }, true);
})();

/* ==========================================================================
   CHAIN-E (2026-10-10) - the cart drawer / page re-render after every change; date their arrival line again.
   ========================================================================== */
(function () {
  var q = false;
  function soon() { if (q) return; q = true; setTimeout(function () { q = false; if (window.rlArrive) window.rlArrive(); }, 120); }
  function boot() {
    document.querySelectorAll('cart-drawer-component, .cart-page, main[data-template="cart"]').forEach(function (n) {
      new MutationObserver(function () { if (n.querySelector('[data-rl-arrive]:not([data-rl-arrive-done])')) soon(); }).observe(n, { childList: true, subtree: true });
    });
    document.addEventListener('cart:update', soon);
    soon();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();

/* ==========================================================================
   STORIES (2026-10-10) - phones: the "See the piece" bar shows once the big photo has scrolled away
   and hides again while the piece card or the footer is on screen.
   ========================================================================== */
(function () {
  function boot() {
    var bar = document.querySelector('[data-rl-story-bar]'); if (!bar || !('IntersectionObserver' in window)) return;
    var hero = document.querySelector('[data-rl-story-hero]') || document.querySelector('.rl-story__head');
    var stop = [document.querySelector('[data-rl-story-piece]'), document.querySelector('footer, .shopify-section-group-footer-group')].filter(Boolean);
    var past = false, seen = new Set();
    function set() {
      var on = past && seen.size === 0;
      bar.classList.toggle('is-on', on); bar.setAttribute('aria-hidden', on ? 'false' : 'true');
      var a = bar.querySelector('a'); if (a) a.tabIndex = on ? 0 : -1;
    }
    new IntersectionObserver(function (es) { es.forEach(function (e) { past = !e.isIntersecting && e.boundingClientRect.top < 0; }); set(); }).observe(hero);
    var io = new IntersectionObserver(function (es) { es.forEach(function (e) { if (e.isIntersecting) seen.add(e.target); else seen.delete(e.target); }); set(); });
    stop.forEach(function (n) { io.observe(n); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();

/* STORIES preview (sandbox harness only, marked data-rl-stories-local): the room chips filter the cards in place,
   because /blogs/stories/tagged/<room> only exists once the blog is live. The real blog page uses the native links. */
(function () {
  function boot() {
    var box = document.querySelector('[data-rl-stories-local]'); if (!box) return;
    document.querySelectorAll('.rl-chips .rl-chip').forEach(function (c) {
      c.addEventListener('click', function (e) {
        e.preventDefault();
        var room = c.textContent.trim().toLowerCase(); if (room === 'all') room = '';
        document.querySelectorAll('.rl-chips .rl-chip').forEach(function (x) { x.classList.toggle('is-on', x === c); });
        box.querySelectorAll('.rl-scard').forEach(function (k) {
          k.style.display = (!room || k.dataset.room === room) ? '' : 'none';
          if (room) k.classList.remove('rl-scard--lead'); else if (k === box.querySelector('.rl-scard')) k.classList.add('rl-scard--lead');
        });
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();

/* ==========================================================================
   SAVE AGAIN (2026-10-22, funnel gap W4; ads/funnel-step4-5-2026-10-22/README.md): Shopify's customer form refuses
   an email that is already a customer (HTTP 400), so people already on our list who use "Email me this cart",
   "Email it to yourself" or "Email me this list" were never recorded and got no email. Each of those forms now hands
   the same tags to the Roomlift Forms helper through Shopify's app proxy (/apps/rl-forms/save, signed by Shopify).
   The helper only ADDS tags to that existing customer (the pieces + saved-again); Flow "Saved pieces - already
   subscribed (R0)" then emails their list. New subscribers are untouched (customer_posted=true -> nothing to do).
   ========================================================================== */
(function () {
  'use strict';
  window.rlSaveAgain = function (r, fd) {
    try {
      if (r && r.ok && /[?&]customer_posted=true/.test(r.url || '')) return r;
      var email = String(fd.get('contact[email]') || '').trim();
      var body = { email: email, handles: [], variant: '', source: 'piece', cart: '' };
      String(fd.get('contact[tags]') || '').split(',').forEach(function (t) {
        t = t.trim().toLowerCase();
        if (t === 'saved-list') body.source = 'list';
        else if (t === 'saved-cart') body.source = 'cart';
        else if (t.indexOf('rlsavev-') === 0) body.variant = t.slice(8);
        else if (t.indexOf('rlsave-') === 0) body.handles.push(t.slice(7));
        else if (t.indexOf('rlcart-') === 0) body.cart = t.slice(7);
      });
      if (!email || (!body.handles.length && !body.cart)) return r;
      return fetch('/apps/rl-forms/save', {
        method: 'POST', credentials: 'omit',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      }).then(function () { return r; }, function () { return r; });
    } catch (e) { return r; }
  };
})();

/* ==========================================================================
   CART SAVE (2026-10-17, sandbox first; ads/storefront-fixes-2026-10-17-cart/README.md): "Not ready? Email me this
   cart" in the cart drawer footer (snippets/cart-summary.liquid rl_part 'foot', CSS section 67).
   Send = fetch POST of Shopify's CONTACT form (form_type=contact -> an email to the store inbox, reply-to = the
   shopper) carrying the shopper's email, a cart permalink rebuilt from /cart.js (https://getroomlift.com/cart/
   <variant>:<qty>,...; +?storefront=true opens the cart page instead of checkout), the piece titles, quantities and
   line prices, the total and the page (our copy). Then a POST of Shopify's CUSTOMER form: tags saved-cart,welcome10,
   newsletter + the cart tag rlcart-<variant>x<qty>.<variant>x<qty> (no commas allowed in a tag), accepts_marketing =
   true - the form's note says so, same wording as the 10% pop-up. The new subscriber fires the Messaging automation
   "Welcome new subscribers"; its "Your 10% is here" email turns the rlcart- tag back into /cart/<v>:<q>,<v>:<q>
   ?discount=ROOMLIFT10 ("Open my cart"). Owner 2026-10-17: the link offers the existing welcome code; "Sent" shows
   ROOMLIFT10 (no discount created/changed); the opt-in tick was dropped for the automatic reply (Shopify Messaging
   only emails subscribed customers). Someone already on the list gets no new trigger - our copy covers them.
   A /challenge answer (Shopify bot check) or an unreadable answer falls back to a normal form submit; on the way back
   (?contact_posted=true + session flags) the customer form is posted and the drawer reopens on "Sent". Capture-phase listener + preventDefault +
   stopPropagation = one submit only (Shopify's form bootstrap never sees it; the form also carries data-nocaptcha).
   Horizon's cart morph resets inputs to the server copy: the typed email is put back after every cart update.
   ========================================================================== */
(function () {
  var RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)*\.[^\s@.]{2,}$/;
  var PEND = 'rl_sc_pending', REC = 'rl_sc_rec', ORIGIN = 'https://getroomlift.com';
  var st = { email: '' }, busy = false;
  function sget(k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } }
  function sset(k, v) { try { if (v === null) window.sessionStorage.removeItem(k); else window.sessionStorage.setItem(k, v); } catch (e) {} }
  function parts(d) {
    if (!d) return null;
    return { d: d, form: d.querySelector('#rl-sc-form'), inp: d.querySelector('#rl-sc-email'), btn: d.querySelector('.rl-sc__btn'),
      err: d.querySelector('.rl-sc__err'), ok: d.querySelector('.rl-sc__ok') };
  }
  function money(c, cur) {
    try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur || 'USD' }).format(c / 100); }
    catch (e) { return '$' + (c / 100).toFixed(2); }
  }
  function fail(p, msg) {
    busy = false;
    if (!p || !p.err) return;
    p.btn.disabled = false; p.btn.textContent = 'Send';
    p.err.textContent = msg; p.err.hidden = false;
    p.inp.setAttribute('aria-invalid', 'true');
    p.inp.setAttribute('aria-describedby', 'rl-sc-err rl-sc-note');
  }
  function clearErr(p) {
    if (!p || !p.err || p.err.hidden) return;
    p.err.hidden = true; p.err.textContent = '';
    p.inp.removeAttribute('aria-invalid');
    p.inp.setAttribute('aria-describedby', 'rl-sc-note');
  }
  function ok(p) {
    busy = false;
    if (!p || !p.form) return;
    p.d.open = true; p.form.hidden = true; p.ok.hidden = false; p.d.classList.add('is-sent');
    try { document.dispatchEvent(new CustomEvent('rl:cart-email-sent')); } catch (e) {}   // assets/rl-measure.js counts it
  }
  function payload(cart, email) {
    var ids = cart.items.map(function (i) { return i.variant_id + ':' + i.quantity; }).join(',');
    var link = ORIGIN + '/cart/' + ids;
    // the same cart as one customer tag (tags: no commas, 255 chars max); too long -> the email links /cart instead
    var tag = 'rlcart-' + cart.items.map(function (i) { return i.variant_id + 'x' + i.quantity; }).join('.');
    if (tag.length > 250) tag = 'rlcart-many';
    var lines = cart.items.map(function (i) {
      var t = i.product_title + (i.variant_title && !/^Default Title$/i.test(i.variant_title) ? ' - ' + i.variant_title : '');
      return '- ' + t + ' x ' + i.quantity + ' - ' + money(i.final_line_price, cart.currency) + (i.url ? ' - ' + ORIGIN + i.url.split('?')[0] : '');
    });
    var text = [
      'Saved cart, sent from the cart drawer ("Not ready? Email me this cart + 10% off your first order").',
      'Shopper email: ' + email,
      'Marketing emails: agreed by sending (the form note says so). Also posted to Shopify as a customer tagged saved-cart, welcome10, newsletter, ' + tag + ' - the "Your 10% is here" welcome email carries the cart link.',
      'If that customer is missing in Shopify, or was already on the list (no welcome email then), reply to this email by hand with the cart link and the code.',
      'Shown on screen: code ROOMLIFT10 (10% off the first order)',
      '',
      'Cart link - opens checkout with these pieces: ' + link,
      'Cart link - opens the cart page instead: ' + link + '?storefront=true',
      '',
      'Pieces:'
    ].concat(lines, ['', 'Estimated total: ' + money(cart.total_price, cart.currency), 'Page: ' + location.origin + location.pathname]).join('\n');
    return { link: link, tag: tag, text: text };
  }
  function nativeSubmit(fd) {
    var f = document.createElement('form');
    f.method = 'post'; f.action = '/contact#rl-sc-form'; f.style.display = 'none';
    fd.forEach(function (v, k) { var i = document.createElement('input'); i.type = 'hidden'; i.name = k; i.value = v; f.appendChild(i); });
    document.body.appendChild(f);
    sset(PEND, '1');
    HTMLFormElement.prototype.submit.call(f);
  }
  function record(email, tag) {
    var c = new FormData();
    c.append('form_type', 'customer'); c.append('utf8', '✓');
    c.append('contact[email]', email);
    c.append('contact[tags]', 'saved-cart,welcome10,newsletter' + (tag ? ',' + tag : ''));
    c.append('contact[accepts_marketing]', 'true');
    return fetch('/contact', { method: 'POST', body: c, credentials: 'same-origin', headers: { Accept: 'text/html' } }).then(function (r) { return window.rlSaveAgain ? window.rlSaveAgain(r, c) : r; }).catch(function () {});
  }

  document.addEventListener('submit', function (ev) {
    var form = ev.target;
    if (!form || form.id !== 'rl-sc-form') return;
    ev.preventDefault(); ev.stopPropagation();
    var p = parts(form.closest('[data-rl-sc]'));
    if (!p || busy) return;
    var v = (p.inp.value || '').trim();
    if (!RE.test(v)) { fail(p, 'Please enter a valid email address, like name@example.com.'); p.inp.focus(); return; }
    p.inp.value = v; st.email = v; clearErr(p);
    if (!window.fetch || !window.FormData) { return fail(p, 'Your browser could not send this. Please email support@getroomlift.com.'); }
    busy = true; p.btn.disabled = true; p.btn.textContent = 'Sending…';
    var fd, pl;
    fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.items || !cart.items.length) throw new Error('empty cart');
        pl = payload(cart, v);
        fd = new FormData(form);                       // form_type=contact, utf8, contact[Form], contact[email]
        fd.set('contact[email]', v);
        fd.set('contact[Cart link]', pl.link);
        fd.set('contact[body]', pl.text);
        return fetch(form.getAttribute('action') || '/contact', { method: 'POST', body: fd, credentials: 'same-origin', headers: { Accept: 'text/html' } });
      })
      .then(function (r) { return r.text().then(function (t) { return { r: r, t: t }; }); })
      .then(function (x) {
        var u = x.r.url || '';
        if (/\/challenge/.test(u) || !x.r.ok) { sset(REC, JSON.stringify({ e: v, t: pl.tag })); return nativeSubmit(fd); }
        if (/[?&]contact_posted=true/.test(u)) return record(v, pl.tag).then(function () { ok(p); });
        var m = /data-rl-sc-ferr[^>]*>([\s\S]*?)<\/span>/.exec(x.t);
        if (m) {
          var tpl = document.createElement('template'); tpl.innerHTML = m[1];   // inert: nothing in it loads or runs
          return fail(p, (tpl.content.textContent || '').replace(/\s+/g, ' ').trim() || 'That did not go through. Please check the address and try again.');
        }
        sset(REC, JSON.stringify({ e: v, t: pl.tag }));
        nativeSubmit(fd);
      })
      .catch(function () { fail(p, 'That did not go through. Please try again, or email support@getroomlift.com.'); });
  }, true);

  // open the form from the link: focus the field in the same tap so the phone keyboard comes up
  document.addEventListener('click', function (ev) {
    var s = ev.target && ev.target.closest && ev.target.closest('.rl-sc__toggle');
    if (!s) return;
    var p = parts(s.closest('[data-rl-sc]'));
    if (!p || p.d.open) return;
    ev.preventDefault();
    p.d.open = true;
    if (p.inp && !p.form.hidden) { try { p.inp.focus({ preventScroll: true }); } catch (e) { p.inp.focus(); } }
    try { (p.form.hidden ? p.ok : p.form).scrollIntoView({ block: 'nearest' }); } catch (e) {}
  });
  document.addEventListener('input', function (ev) {
    if (ev.target && ev.target.id === 'rl-sc-email') { st.email = ev.target.value; clearErr(parts(ev.target.closest('[data-rl-sc]'))); }
  });

  // Horizon's morph after a cart change puts the server copy back (empty field): restore it
  var q = null;
  function restore() {
    q = null;
    var p = parts(document.querySelector('[data-rl-sc]'));
    if (!p || !p.inp) return;
    if (st.email && !p.inp.value && document.activeElement !== p.inp) p.inp.value = st.email;
  }
  function soon() { if (!q) q = setTimeout(restore, 60); }
  function boot() {
    var cd = document.querySelector('cart-drawer-component');
    if (cd && window.MutationObserver) new MutationObserver(soon).observe(cd, { childList: true, subtree: true, attributes: true, attributeFilter: ['value'] });
    document.addEventListener('cart:update', function () { setTimeout(restore, 120); setTimeout(restore, 500); });
    // back from the normal-submit fallback (bot check): reopen the drawer on "Sent"
    if (/[?&]contact_posted=true/.test(location.search) && sget(PEND) === '1') {
      sset(PEND, null);
      var rec = null; try { rec = JSON.parse(sget(REC) || 'null'); } catch (e) {}
      sset(REC, null);
      if (rec && rec.e) record(rec.e, rec.t);
      try { history.replaceState(history.state, '', location.pathname + location.search.replace(/([?&])contact_posted=true(&|$)/, function (m, a, b) { return b ? a : ''; }).replace(/[?&]$/, '')); } catch (e) {}
      var go = function () {
        var c = document.querySelector('cart-drawer-component');
        if (c && typeof c.open === 'function') c.open();
        ok(parts(document.querySelector('[data-rl-sc]')));
      };
      if (window.customElements && customElements.whenDefined) customElements.whenDefined('cart-drawer-component').then(function () { setTimeout(go, 300); }); else setTimeout(go, 800);
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();

/* ==========================================================================
   CART SAVE + PHONE KEYBOARD (owner 2026-10-18, Android Chrome: tapping the email box opened the keyboard OVER it -
   the drawer dialog keeps the full layout height while the keyboard only shrinks the visual viewport). While the
   email box is focused and a keyboard is up (visual viewport > 120 px shorter than the window), the dialog is sized
   to the visual viewport and the bottom bar gets class rl-kb (hidden, CSS section 67) so the strip with the form under
   the items has the room; the box is scrolled into view. Everything is put back when the box loses focus or the
   keyboard closes. No visualViewport (old browsers) = no change.
   ========================================================================== */
(function () {
  var vv = window.visualViewport;
  if (!vv) return;
  var on = false, t1 = null;
  function parts() {
    var d = document.querySelector('cart-drawer-component .cart-drawer__dialog');
    return d ? { d: d, s: d.querySelector('.cart-drawer__summary') } : null;
  }
  function reset(p) {
    if (!on || !p) return;
    ['height', 'max-height', 'top', 'bottom'].forEach(function (k) { p.d.style.removeProperty(k); });
    if (p.s) p.s.classList.remove('rl-kb');
    on = false;
  }
  function fit() {
    var p = parts();
    if (!p) return;
    var a = document.activeElement;
    var typing = a && a.id === 'rl-sc-email';
    var kb = window.innerHeight - vv.height > 120;
    if (!(typing && kb)) { reset(p); return; }
    p.d.style.setProperty('height', vv.height + 'px', 'important');
    p.d.style.setProperty('max-height', vv.height + 'px', 'important');
    p.d.style.setProperty('top', vv.offsetTop + 'px', 'important');
    p.d.style.setProperty('bottom', 'auto', 'important');
    if (p.s) p.s.classList.add('rl-kb');
    on = true;
    try { a.scrollIntoView({ block: 'nearest' }); } catch (e) {}
  }
  function soon() { clearTimeout(t1); t1 = setTimeout(fit, 60); }
  vv.addEventListener('resize', soon);
  vv.addEventListener('scroll', soon);
  document.addEventListener('focusin', function (e) { if (e.target && e.target.id === 'rl-sc-email') { soon(); setTimeout(fit, 400); } });
  document.addEventListener('focusout', function (e) { if (e.target && e.target.id === 'rl-sc-email') setTimeout(fit, 150); });
})();

/* ==========================================================================
   PDP HOOK LINE TAP (owner 2026-10-18): the line under the product title is held to one line on phones (first-screen
   budget, CSS 35d/36a) and showed "..." that did nothing on tap. A clipped hook now gets .is-clipped (+ role=button,
   aria-expanded=false, tabindex) and opens in place on tap / Enter / Space (.is-open, CSS section 69).
   ========================================================================== */
(function () {
  function clipped(h) { return h.scrollWidth > h.clientWidth + 1 || h.scrollHeight > h.clientHeight + 1; }
  function mark() {
    var hs = document.querySelectorAll('.rl-hook');
    for (var i = 0; i < hs.length; i++) {
      var h = hs[i];
      if (h.classList.contains('is-open')) continue;
      var c = clipped(h);
      h.classList.toggle('is-clipped', c);
      if (c) { h.setAttribute('role', 'button'); h.setAttribute('tabindex', '0'); h.setAttribute('aria-expanded', 'false'); }
      else { h.removeAttribute('role'); h.removeAttribute('tabindex'); h.removeAttribute('aria-expanded'); }
    }
  }
  function open(h) {
    h.classList.add('is-open'); h.classList.remove('is-clipped');
    h.setAttribute('aria-expanded', 'true'); h.removeAttribute('role'); h.removeAttribute('tabindex');
  }
  document.addEventListener('click', function (e) {
    var h = e.target && e.target.closest && e.target.closest('.rl-hook.is-clipped');
    if (h) open(h);
  });
  document.addEventListener('keydown', function (e) {
    var h = e.target;
    if ((e.key === 'Enter' || e.key === ' ') && h && h.classList && h.classList.contains('rl-hook') && h.classList.contains('is-clipped')) { e.preventDefault(); open(h); }
  });
  var t = null;
  function soon() { clearTimeout(t); t = setTimeout(mark, 150); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mark); else mark();
  window.addEventListener('load', mark);
  window.addEventListener('resize', soon);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(mark);
})();
/* CHAT LINK (2026-10-21, audit ads/funnel-audit-2026-10-21 #1): the cart's "Questions? Chat with us" link
   (snippets/cart-summary.liquid, drawer + cart page) opens Shopify Inbox instead of the mail app. The drawer is a modal
   dialog in the top layer and would cover the chat, so it closes first. No chat on the page (app embed off or not loaded
   yet) -> nothing is intercepted and the link's own mailto: runs. Clarity event rl_chat_tap. */
(function () {
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('[data-rl-chat]') : null;
    if (!a) return;
    var chat = document.querySelector('shopify-chat');
    if (!chat || typeof chat.show !== 'function') return;
    e.preventDefault();
    try { if (window.clarity) window.clarity('event', 'rl_chat_tap'); } catch (err) {}
    var open = function () {
      try { chat.show(); } catch (err) { window.location.href = a.getAttribute('href'); }
    };
    var drawer = a.closest('cart-drawer-component');
    if (drawer && typeof drawer.close === 'function') {
      drawer.close();
      setTimeout(open, 300);
    } else {
      open();
    }
  });
})();

/* ==========================================================================
   SAVE PIECE (2026-10-22, owner: "yes build step 5 on sandbox"; plan ads/funnel-plan-2026-10-22.md step 3): "Not ready?
   Save this piece" under the buy buttons (block rl_save, CSS 70e). The same two posts as CART SAVE: Shopify's CONTACT form
   (a copy to the store inbox: the piece, the variant, the link) then Shopify's CUSTOMER form (tags saved-piece, welcome10,
   newsletter, rlsave-<handle>, rlsavev-<variant>; accepts_marketing = true, the note says so) -> the Messaging automation
   "Welcome new subscribers" -> "Your 10% is here", whose custom block turns the rlsave- tags back into a link to the piece
   (ads/storefront-fixes-2026-10-22-save/email-block.liquid). Someone already on the list gets no new welcome email - the
   store copy covers them. A bot check (/challenge) or an unreadable answer falls back to a normal form submit; back on the
   page (?contact_posted=true + session flags) the customer form is posted and the box shows "Saved". While the email field
   has focus on a phone, html.rl-sp-typing moves the sticky Add-to-cart bar aside so the keyboard never parks it over the
   field. Clarity event rl_piece_saved.
   ========================================================================== */
(function () {
  var RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)*\.[^\s@.]{2,}$/;
  var PEND = 'rl_sp_pending', REC = 'rl_sp_rec', ORIGIN = 'https://getroomlift.com';
  var busy = false;
  function sget(k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } }
  function sset(k, v) { try { if (v === null) window.sessionStorage.removeItem(k); else window.sessionStorage.setItem(k, v); } catch (e) {} }
  function parts(b) {
    if (!b) return null;
    return { b: b, form: b.querySelector('#rl-sp-form'), inp: b.querySelector('#rl-sp-email'), btn: b.querySelector('.rl-sp__btn'),
      err: b.querySelector('.rl-sp__err'), ok: b.querySelector('.rl-sp__ok') };
  }
  function fail(p, msg) {
    busy = false;
    if (!p || !p.err) return;
    p.btn.disabled = false; p.btn.textContent = 'Send';
    p.err.textContent = msg; p.err.hidden = false;
    p.inp.setAttribute('aria-invalid', 'true');
    p.inp.setAttribute('aria-describedby', 'rl-sp-err rl-sp-note');
  }
  function clearErr(p) {
    if (!p || !p.err || p.err.hidden) return;
    p.err.hidden = true; p.err.textContent = '';
    p.inp.removeAttribute('aria-invalid');
    p.inp.setAttribute('aria-describedby', 'rl-sp-note');
  }
  function done(p) {
    busy = false;
    if (!p || !p.form) return;
    p.form.hidden = true; p.ok.hidden = false; p.b.classList.add('is-sent');
    document.documentElement.classList.remove('rl-sp-typing');
    try { if (window.clarity) window.clarity('event', 'rl_piece_saved'); } catch (e) {}
  }
  function variantId(p) {
    var u = null;
    try { u = new URLSearchParams(location.search).get('variant'); } catch (e) {}
    if (u && /^\d+$/.test(u)) return u;
    var f = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    if (f && /^\d+$/.test(f.value)) return f.value;
    return p.b.getAttribute('data-variant') || '';
  }
  function info(p, email) {
    var handle = p.b.getAttribute('data-handle') || '';
    var vid = variantId(p);
    var link = ORIGIN + '/products/' + handle + (vid ? '?variant=' + vid : '');
    var tags = 'saved-piece,welcome10,newsletter,rlsave-' + handle + (vid ? ',rlsavev-' + vid : '');
    // 2026-10-22 wishlist: "Email it to yourself" also carries the pieces already saved with "♡ Save" (WISHLIST + SHARE)
    try { (window.rlWishlist ? window.rlWishlist.get() : []).forEach(function (it) { if (it.h && it.h !== handle && tags.length < 900) tags += ',rlsave-' + it.h; }); } catch (e) {}
    var text = [
      'Saved piece, sent from the product page ("Not ready? Save this piece").',
      'Shopper email: ' + email,
      'Piece: ' + (p.b.getAttribute('data-title') || handle) + (vid ? ' (variant ' + vid + ')' : ''),
      'Link: ' + link,
      'Marketing emails: agreed by saving (the form note says so). Also posted to Shopify as a customer tagged ' + tags + ' - the "Your 10% is here" welcome email links the piece.',
      'If that customer is missing in Shopify, or was already on the list (no welcome email then), reply to this email by hand with the link and the code ROOMLIFT10.'
    ].join('\n');
    return { link: link, tags: tags, text: text };
  }
  function record(email, tags) {
    var c = new FormData();
    c.append('form_type', 'customer'); c.append('utf8', '✓');
    c.append('contact[email]', email);
    c.append('contact[tags]', tags);
    c.append('contact[accepts_marketing]', 'true');
    return fetch('/contact', { method: 'POST', body: c, credentials: 'same-origin', headers: { Accept: 'text/html' } }).then(function (r) { return window.rlSaveAgain ? window.rlSaveAgain(r, c) : r; }).catch(function () {});
  }
  function nativeSubmit(fd) {
    var f = document.createElement('form');
    f.method = 'post'; f.action = '/contact#rl-sp-form'; f.style.display = 'none';
    fd.forEach(function (v, k) { var i = document.createElement('input'); i.type = 'hidden'; i.name = k; i.value = v; f.appendChild(i); });
    document.body.appendChild(f);
    sset(PEND, location.pathname);
    HTMLFormElement.prototype.submit.call(f);
  }

  document.addEventListener('submit', function (ev) {
    var form = ev.target;
    if (!form || form.id !== 'rl-sp-form') return;
    ev.preventDefault(); ev.stopPropagation();
    var p = parts(form.closest('[data-rl-sp]'));
    if (!p || busy) return;
    var v = (p.inp.value || '').trim();
    if (!RE.test(v)) { fail(p, 'Please enter a valid email address, like name@example.com.'); p.inp.focus(); return; }
    p.inp.value = v; clearErr(p);
    if (!window.fetch || !window.FormData) { return fail(p, 'Your browser could not send this. Please email support@getroomlift.com.'); }
    busy = true; p.btn.disabled = true; p.btn.textContent = 'Sending…';
    var x = info(p, v);
    var fd = new FormData(form);                       // form_type=contact, utf8, contact[Form], contact[email]
    fd.set('contact[email]', v);
    fd.set('contact[Piece link]', x.link);
    fd.set('contact[body]', x.text);
    fetch(form.getAttribute('action') || '/contact', { method: 'POST', body: fd, credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then(function (r) { return r.text().then(function (t) { return { r: r, t: t }; }); })
      .then(function (res) {
        var u = res.r.url || '';
        if (/\/challenge/.test(u) || !res.r.ok) { sset(REC, JSON.stringify({ e: v, t: x.tags })); return nativeSubmit(fd); }
        if (/[?&]contact_posted=true/.test(u)) return record(v, x.tags).then(function () { done(p); });
        var m = /data-rl-sp-ferr[^>]*>([\s\S]*?)<\/span>/.exec(res.t);
        if (m) {
          var tpl = document.createElement('template'); tpl.innerHTML = m[1];   // inert: nothing in it loads or runs
          return fail(p, (tpl.content.textContent || '').replace(/\s+/g, ' ').trim() || 'That did not go through. Please check the address and try again.');
        }
        sset(REC, JSON.stringify({ e: v, t: x.tags }));
        nativeSubmit(fd);
      })
      .catch(function () { fail(p, 'That did not go through. Please try again, or email support@getroomlift.com.'); });
  }, true);

  document.addEventListener('input', function (ev) {
    if (ev.target && ev.target.id === 'rl-sp-email') clearErr(parts(ev.target.closest('[data-rl-sp]')));
  });
  // phone keyboard: the sticky Add-to-cart bar steps aside while the field has focus (CSS 70e)
  document.addEventListener('focusin', function (ev) {
    if (ev.target && ev.target.id === 'rl-sp-email') document.documentElement.classList.add('rl-sp-typing');
  });
  document.addEventListener('focusout', function (ev) {
    if (ev.target && ev.target.id === 'rl-sp-email') document.documentElement.classList.remove('rl-sp-typing');
  });

  // back from the normal-submit fallback (bot check): post the customer form and show "Saved"
  function boot() {
    if (!/[?&]contact_posted=true/.test(location.search) || sget(PEND) !== location.pathname) return;
    sset(PEND, null);
    var rec = null; try { rec = JSON.parse(sget(REC) || 'null'); } catch (e) {}
    sset(REC, null);
    if (rec && rec.e) record(rec.e, rec.t);
    try { history.replaceState(history.state, '', location.pathname + location.search.replace(/([?&])contact_posted=true(&|$)/, function (m, a, b) { return b ? a : ''; }).replace(/[?&]$/, '')); } catch (e) {}
    var p = parts(document.querySelector('[data-rl-sp]'));
    done(p);
    if (p && p.b) { try { p.b.scrollIntoView({ block: 'center' }); } catch (e) {} }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();

/* ==========================================================================
   WISHLIST + SHARE (2026-10-22, owner: "plan approved"; ads/wishlist-share-plan-2026-10-22.md). No app, no sign-up.
   - Product page (block rl_sv, under the buy buttons): "Save" keeps the piece in this browser's list (localStorage
     rl_sv, max 20, newest first); "Share" opens the phone's share menu (Web Share) with "What do you think of this for
     our home? <piece>" + the link; where there is no share menu, a small panel: WhatsApp, Text message, Email, Copy link.
   - Header heart (snippets/header-actions.liquid) with the count, hidden while the list is empty.
   - /pages/saved (template page.saved): the list with today's price and stock from /products/<handle>.js; Add to cart,
     Remove, "Add all to cart" (every in-stock piece in one go; sold-out ones are skipped and named), "Share this list"
     (one link: /pages/saved?items=<handle>~<variant>,...) and "Email me this list" (the same two Shopify forms as
     "Save this piece": a contact copy to support@ + the customer form with one rlsave- tag per piece). A shared link opens
     "Shared with you" with "Add all to cart" and "Save to my list".
   - Shared links carry utm_source=share (utm_medium product / list) so Shopify counts the visits they bring.
   - Clarity events: rl_save_add, rl_save_remove, rl_share_tap, rl_list_view, rl_list_share, rl_list_addall,
     rl_list_email, rl_shared_open, rl_shared_keep.
   window.rlWishlist.get() is read by SAVE PIECE, so "Email it to yourself" also emails the saved pieces.
   ========================================================================== */
(function () {
  var KEY = 'rl_sv', MAX = 20, ORIGIN = 'https://getroomlift.com', PEND = 'rl_sv_pending', REC = 'rl_sv_rec';
  var RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)*\.[^\s@.]{2,}$/;
  function ev(n) { try { if (window.clarity) window.clarity('event', n); } catch (e) {} }
  function sget(k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } }
  function sset(k, v) { try { if (v === null) window.sessionStorage.removeItem(k); else window.sessionStorage.setItem(k, v); } catch (e) {} }
  function load() {
    try {
      var a = JSON.parse(window.localStorage.getItem(KEY) || '[]');
      return Array.isArray(a) ? a.filter(function (x) { return x && typeof x.h === 'string' && x.h; }) : [];
    } catch (e) { return []; }
  }
  function store(a) { try { window.localStorage.setItem(KEY, JSON.stringify(a.slice(0, MAX))); } catch (e) {} paintHead(); }
  function has(h) { return load().some(function (x) { return x.h === h; }); }
  function add(it) { var a = load().filter(function (x) { return x.h !== it.h; }); a.unshift(it); store(a); }
  function drop(h) { store(load().filter(function (x) { return x.h !== h; })); }
  window.rlWishlist = { get: load };

  // ---- card hearts (block rl_svheart on collection + search cards; owner 2026-10-22 "heart on product card")
  function cardPaint() {
    document.querySelectorAll('[data-rl-sv-card]').forEach(function (b) {
      b.setAttribute('aria-pressed', has(b.getAttribute('data-handle')) ? 'true' : 'false');
    });
  }
  // capture phase: the heart sits over the card's link and quick-add, so it must stop the tap before they see it
  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest ? e.target.closest('[data-rl-sv-card]') : null;
    if (!b) return;
    e.preventDefault(); e.stopPropagation();
    var h = b.getAttribute('data-handle');
    if (has(h)) { drop(h); ev('rl_save_remove'); toast('Removed from your saved pieces.'); }
    else if (load().length >= MAX) { toast('Your list is full (20 pieces). <a href="/pages/saved">Remove one</a> first.'); }
    else { add({ h: h, v: b.getAttribute('data-variant') || '', ts: Date.now() }); ev('rl_save_add_card'); toast(savedMsg()); }
    cardPaint();
  }, true);
  // short note under the header after a save / remove (cards; the product page on a phone)
  function savedMsg() { return 'Saved &middot; <a href="/pages/saved">See your list (' + load().length + ')</a>'; }
  function toast(html) {
    var t = document.getElementById('rl-sv-toast');
    if (!t) {
      t = document.createElement('div'); t.id = 'rl-sv-toast'; t.className = 'rl-sv-toast';
      t.setAttribute('role', 'status'); t.setAttribute('aria-live', 'polite'); document.body.appendChild(t);
    }
    t.innerHTML = html; t.classList.add('is-on');
    clearTimeout(t.rlT); t.rlT = setTimeout(function () { t.classList.remove('is-on'); }, 3500);
  }
  function phone() { return !!(window.matchMedia && window.matchMedia('(max-width: 749px)').matches); }
  // card share (owner 2026-10-22 "i also want to add share there"): the phone's share menu, else a centred box of links
  var shLast = null;
  function sheetClose() {
    var s = document.getElementById('rl-sv-sheet'); if (!s || s.hidden) return;
    s.hidden = true; if (shLast) { try { shLast.focus({ preventScroll: true }); } catch (x) {} }
  }
  function sheetOpen(name, url, text, subject) {
    shLast = document.activeElement;
    var s = document.getElementById('rl-sv-sheet');
    if (!s) {
      s = document.createElement('div'); s.id = 'rl-sv-sheet'; s.className = 'rl-sv-sheet'; s.hidden = true;
      s.innerHTML = '<div class="rl-sv-sheet__box" role="dialog" aria-modal="true" aria-labelledby="rl-sv-sheet-t">' +
        '<p class="rl-sv-sheet__t" id="rl-sv-sheet-t"></p><div class="rl-sv-panel" data-rl-sv-panel hidden></div>' +
        '<button type="button" class="rl-sv-sheet__x" data-rl-sv-sheet-x>Close</button></div>';
      s.addEventListener('click', function (e) { if (e.target === s || (e.target.closest && e.target.closest('[data-rl-sv-sheet-x]'))) sheetClose(); });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape') sheetClose(); });
      document.body.appendChild(s);
    }
    s.querySelector('#rl-sv-sheet-t').textContent = 'Share ' + name;
    panel(s.querySelector('[data-rl-sv-panel]'), url, text, subject);
    s.hidden = false;
    var f = s.querySelector('[data-rl-sv-panel] a'); if (f) { try { f.focus({ preventScroll: true }); } catch (x) {} }
  }
  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest ? e.target.closest('[data-rl-sv-cshare]') : null;
    if (!b) return;
    e.preventDefault(); e.stopPropagation();

    var name = b.getAttribute('data-title') || 'this piece';
    var url = utm(ORIGIN + '/products/' + b.getAttribute('data-handle'), 'card');
    ev('rl_share_tap_card');
    shareOrSheet(name, url);
  }, true);
  function shareOrSheet(name, url) {
    var text = 'What do you think of this for our home? ' + name, subject = name + ' - Roomlift';
    if (navigator.share) {
      navigator.share({ title: subject, text: text, url: url }).catch(function (x) { if (!x || x.name !== 'AbortError') sheetOpen(name, url, text, subject); });
      return;
    }
    sheetOpen(name, url, text, subject);
  }
  // homepage card Add to cart (snippet rl-tile-acts, owner 2026-10-23): add the one-version piece, refresh the drawer
  // with the same section render the theme uses, open the drawer; on failure say so and offer the product page
  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest ? e.target.closest('[data-rl-home-add]') : null;
    if (!b) return;
    e.preventDefault(); e.stopPropagation();
    if (b.getAttribute('aria-busy') === 'true') return;
    b.setAttribute('aria-busy', 'true');
    var list = document.querySelector('cart-items-component');
    var sid = list && list.dataset ? list.dataset.sectionId : '';
    var body = { items: [{ id: Number(b.getAttribute('data-rl-home-add')), quantity: 1 }] };
    if (sid) body.sections = sid;
    fetch('/cart/add.js', { method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) })
      .then(function (r) { if (!r.ok) throw new Error('add'); return r.json(); })
      .then(function (res) {
        return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); })
          .then(function (cart) {
            document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { resource: cart, sourceId: 'rl-home-add',
              data: { source: 'rl-home-add', itemCount: cart.item_count, sections: (res && res.sections) || {} } } }));
            ev('rl_home_add');
            var d = document.querySelector('cart-drawer-component');
            if (d && typeof d.open === 'function') d.open(); else location.href = '/cart';
          });
      })
      .catch(function () { toast('That piece could not be added. <a href="' + esc(b.getAttribute('data-href') || '/cart') + '">Open it</a>'); })
      .then(function () { b.removeAttribute('aria-busy'); });
  }, true);
  var cq = null;
  if (window.MutationObserver) {
    new MutationObserver(function () { if (!cq) cq = setTimeout(function () { cq = null; cardPaint(); }, 60); })
      .observe(document.documentElement, { childList: true, subtree: true });
  }

  function paintHead() {
    var n = load().length;
    document.querySelectorAll('[data-rl-sv-head]').forEach(function (a) {
      a.hidden = n === 0;
      var c = a.querySelector('[data-rl-sv-n]'); if (c) c.textContent = n ? String(n) : '';
      a.setAttribute('aria-label', 'Your saved pieces (' + n + ')');
    });
  }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
  function money(c) { try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(c / 100); } catch (e) { return '$' + (c / 100).toFixed(2); } }
  function img(src, w) {
    var s = typeof src === 'string' ? src : ((src && src.src) || '');
    if (!s) return '';
    if (s.indexOf('//') === 0) s = 'https:' + s;
    return s + (s.indexOf('?') < 0 ? '?' : '&') + 'width=' + w;
  }
  function utm(u, medium) { return u + (u.indexOf('?') < 0 ? '?' : '&') + 'utm_source=share&utm_medium=' + medium + '&utm_campaign=roomlift-share'; }
  function variantOf(box) {
    var u = null;
    try { u = new URLSearchParams(location.search).get('variant'); } catch (e) {}
    if (u && /^\d+$/.test(u)) return u;
    var f = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    if (f && /^\d+$/.test(f.value)) return f.value;
    return box.getAttribute('data-variant') || '';
  }

  // ---- share: the phone's own share menu; otherwise a small panel of links
  function panel(p, url, text, subject) {
    var enc = encodeURIComponent;
    p.innerHTML = '';
    [['WhatsApp', 'https://wa.me/?text=' + enc(text + ' ' + url)],
     ['Text message', 'sms:?&body=' + enc(text + ' ' + url)],
     ['Email', 'mailto:?subject=' + enc(subject) + '&body=' + enc(text + '\n\n' + url)]].forEach(function (l) {
      var a = document.createElement('a'); a.href = l[1]; a.textContent = l[0];
      if (/^https/.test(l[1])) { a.target = '_blank'; a.rel = 'noopener'; }
      p.appendChild(a);
    });
    var b = document.createElement('button'); b.type = 'button'; b.textContent = 'Copy link';
    b.addEventListener('click', function () {
      var done = function () { b.textContent = 'Link copied'; };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(done, function () { window.prompt('Copy this link:', url); });
      else window.prompt('Copy this link:', url);
    });
    p.appendChild(b);
    p.hidden = false;
  }
  function share(btn, p, url, text, subject, evName) {
    ev(evName);
    if (navigator.share) {
      navigator.share({ title: subject, text: text, url: url }).catch(function (e) {
        if (!e || e.name !== 'AbortError') { panel(p, url, text, subject); btn.setAttribute('aria-expanded', 'true'); }
      });
      return;
    }
    if (p.hidden) { panel(p, url, text, subject); btn.setAttribute('aria-expanded', 'true'); }
    else { p.hidden = true; btn.setAttribute('aria-expanded', 'false'); }
  }

  // ---- product page
  function pdPaint(box) {
    var on = has(box.getAttribute('data-handle'));
    var b = box.querySelector('[data-rl-sv-save]'); if (!b) return;
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
    var l = b.querySelector('[data-rl-sv-save-label]'); if (l) l.textContent = on ? 'Saved' : 'Save';
  }
  function pdNote(box, html) {
    var n = box.querySelector('[data-rl-sv-note]'); if (!n) return;
    if (html && phone()) toast(html); // on a phone the buttons sit on the photo and the inline note is hidden
    if (!html) { n.hidden = true; n.innerHTML = ''; return; }
    n.innerHTML = html; n.hidden = false;
  }

  // ---- saved pieces page
  var pg = { rows: [], shared: false };
  function sharedItems() {
    var q = null;
    try { q = new URLSearchParams(location.search).get('items'); } catch (e) {}
    if (!q) return null;
    return q.split(',').map(function (s) {
      var p = s.split('~');
      return { h: (p[0] || '').replace(/[^a-z0-9-]/gi, '').toLowerCase(), v: (p[1] || '').replace(/\D/g, '') };
    }).filter(function (x) { return x.h; }).slice(0, MAX);
  }
  function fetchItem(it) {
    return fetch('/products/' + it.h + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('gone'); return r.json(); })
      .then(function (p) {
        var vs = p.variants || [], v = null;
        vs.forEach(function (x) { if (String(x.id) === String(it.v)) v = x; });
        if (!v) v = vs.filter(function (x) { return x.available; })[0] || vs[0];
        return { h: it.h, ok: true, title: p.title, vt: v && v.title && !/^Default Title$/i.test(v.title) ? v.title : '',
          id: v ? v.id : null, price: v ? v.price : p.price, available: !!(v && v.available),
          image: (v && v.featured_image && v.featured_image.src) || p.featured_image, url: '/products/' + it.h + (v ? '?variant=' + v.id : '') };
      })
      .catch(function () { return { h: it.h, ok: false }; });
  }
  function row(x, shared) {
    var li = document.createElement('li'); li.className = 'rl-sv__item'; li.setAttribute('data-h', x.h);
    if (!x.ok) {
      li.innerHTML = '<div class="rl-sv__info"><p class="rl-sv__t">This piece is no longer available.</p>' +
        (shared ? '' : '<div class="rl-sv__line"><span></span><span class="rl-sv__do"><button type="button" class="rl-sv__rm" data-rl-sv-rm>Remove</button></span></div>') + '</div>';
      return li;
    }
    li.innerHTML = '<a class="rl-sv__img" href="' + esc(x.url) + '" tabindex="-1" aria-hidden="true"><img src="' + esc(img(x.image, 240)) + '" alt="" loading="lazy" width="96" height="96"></a>' +
      '<div class="rl-sv__info"><a class="rl-sv__t" href="' + esc(x.url) + '">' + esc(x.title) + '</a>' +
      '<p class="rl-sv__m">' + (x.vt ? esc(x.vt) + ' &middot; ' : '') + (x.available ? 'In stock' : 'Sold out') + '</p>' +
      '<div class="rl-sv__line"><b class="rl-sv__p">' + money(x.price) + '</b><span class="rl-sv__do">' +
      (shared ? '' : '<button type="button" class="rl-sv__rm" data-rl-sv-rm>Remove</button>') +
      (x.available ? '<button type="button" class="rl-sv__add" data-rl-sv-add="' + esc(x.id) + '">Add to cart</button>' : '') +
      '</span></div></div>';
    return li;
  }
  function msg(root, html) { var m = root.querySelector('[data-rl-sv-msg]'); if (m) m.innerHTML = html || ''; }
  function renderPage(root) {
    var shared = sharedItems(); pg.shared = !!shared;
    var items = shared || load();
    var title = root.querySelector('[data-rl-sv-title]'), sub = root.querySelector('[data-rl-sv-sub]'),
        acts = root.querySelector('[data-rl-sv-acts]'), list = root.querySelector('[data-rl-sv-list]'),
        empty = root.querySelector('[data-rl-sv-empty]');
    root.querySelector('[data-rl-sv-mine]').hidden = pg.shared;
    root.querySelector('[data-rl-sv-keep]').hidden = !pg.shared;
    if (pg.shared) { title.textContent = 'Shared with you'; ev('rl_shared_open'); } else { ev('rl_list_view'); }
    if (!items.length) {
      sub.textContent = pg.shared ? 'This link has no pieces in it.' : '';
      acts.hidden = true; list.innerHTML = ''; empty.hidden = false; pg.rows = [];
      return;
    }
    empty.hidden = true; sub.textContent = 'Loading your pieces…';
    Promise.all(items.map(fetchItem)).then(function (rows) {
      pg.rows = rows; list.innerHTML = '';
      rows.forEach(function (x) { list.appendChild(row(x, pg.shared)); });
      var n = rows.filter(function (x) { return x.ok; }).length;
      sub.textContent = pg.shared
        ? 'Someone picked ' + (n === 1 ? 'this piece' : 'these ' + n + ' pieces') + ' for you to look at.'
        : n + (n === 1 ? ' piece' : ' pieces') + ' · saved on this device, no account needed';
      acts.hidden = false;
      root.querySelector('[data-rl-sv-addall]').hidden = !rows.some(function (x) { return x.ok && x.available; });
    });
  }
  function addToCart(items) {
    return fetch('/cart/add.js', { method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: items }) })
      .then(function (r) { if (!r.ok) throw new Error('add'); return r.json(); });
  }
  function listTags() {
    var t = 'saved-list,welcome10,newsletter';
    pg.rows.forEach(function (x) { if (x.ok && t.length < 900) t += ',rlsave-' + x.h; });
    return t;
  }

  // ---- "Email me this list" (same two posts as SAVE PIECE)
  function wlParts(root) {
    var w = root.querySelector('[data-rl-sv-email]');
    return { w: w, form: w.querySelector('#rl-sv-form'), inp: w.querySelector('#rl-sv-email-inp'), btn: w.querySelector('.rl-sp__btn'),
      err: w.querySelector('.rl-sp__err'), ok: w.querySelector('.rl-sp__ok'), lead: w.querySelector('.rl-sp__lead') };
  }
  var busy = false;
  function wlFail(p, m) {
    busy = false; p.btn.disabled = false; p.btn.textContent = 'Send';
    p.err.textContent = m; p.err.hidden = false; p.inp.setAttribute('aria-invalid', 'true');
  }
  function wlDone(p) {
    busy = false; p.form.hidden = true; if (p.lead) p.lead.hidden = true; p.ok.hidden = false;
    document.documentElement.classList.remove('rl-sp-typing');
    ev('rl_list_email');
  }
  function record(email, tags) {
    var c = new FormData();
    c.append('form_type', 'customer'); c.append('utf8', '✓');
    c.append('contact[email]', email); c.append('contact[tags]', tags); c.append('contact[accepts_marketing]', 'true');
    return fetch('/contact', { method: 'POST', body: c, credentials: 'same-origin', headers: { Accept: 'text/html' } }).then(function (r) { return window.rlSaveAgain ? window.rlSaveAgain(r, c) : r; }).catch(function () {});
  }
  function nativeSubmit(fd) {
    var f = document.createElement('form');
    f.method = 'post'; f.action = '/contact#rl-sv-form'; f.style.display = 'none';
    fd.forEach(function (v, k) { var i = document.createElement('input'); i.type = 'hidden'; i.name = k; i.value = v; f.appendChild(i); });
    document.body.appendChild(f); sset(PEND, location.pathname);
    HTMLFormElement.prototype.submit.call(f);
  }
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.id !== 'rl-sv-form') return;
    e.preventDefault(); e.stopPropagation();
    var root = form.closest('[data-rl-sv]'); if (!root || busy) return;
    var p = wlParts(root), v = (p.inp.value || '').trim();
    if (!RE.test(v)) { wlFail(p, 'Please enter a valid email address, like name@example.com.'); p.inp.focus(); return; }
    p.err.hidden = true; p.inp.removeAttribute('aria-invalid');
    busy = true; p.btn.disabled = true; p.btn.textContent = 'Sending…';
    var tags = listTags();
    var lines = pg.rows.filter(function (x) { return x.ok; }).map(function (x) { return '- ' + x.title + (x.vt ? ' (' + x.vt + ')' : '') + ' - ' + money(x.price) + ' - ' + ORIGIN + x.url; });
    var link = ORIGIN + '/pages/saved?items=' + pg.rows.filter(function (x) { return x.ok; }).map(function (x) { return x.h + '~' + (x.id || ''); }).join(',');
    var fd = new FormData(form);
    fd.set('contact[email]', v);
    fd.set('contact[List link]', link);
    fd.set('contact[body]', ['Saved list, sent from "Your saved pieces" ("Email me this list").', 'Shopper email: ' + v,
      'Marketing emails: agreed by sending (the form note says so). Also posted to Shopify as a customer tagged ' + tags + ' - a NEW subscriber gets the "Your 10% is here" welcome email with the pieces.',
      'Already on the list (no welcome email then)? Reply by hand with the list link and the code ROOMLIFT10.', '', 'List link: ' + link, '', 'Pieces:'].concat(lines).join('\n'));
    fetch(form.getAttribute('action') || '/contact', { method: 'POST', body: fd, credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then(function (r) { return r.text().then(function (t) { return { r: r, t: t }; }); })
      .then(function (res) {
        var u = res.r.url || '';
        if (/\/challenge/.test(u) || !res.r.ok) { sset(REC, JSON.stringify({ e: v, t: tags })); return nativeSubmit(fd); }
        if (/[?&]contact_posted=true/.test(u)) return record(v, tags).then(function () { wlDone(p); });
        var m = /data-rl-sv-ferr[^>]*>([\s\S]*?)<\/span>/.exec(res.t);
        if (m) {
          var tpl = document.createElement('template'); tpl.innerHTML = m[1];
          return wlFail(p, (tpl.content.textContent || '').replace(/\s+/g, ' ').trim() || 'That did not go through. Please check the address and try again.');
        }
        sset(REC, JSON.stringify({ e: v, t: tags })); nativeSubmit(fd);
      })
      .catch(function () { wlFail(p, 'That did not go through. Please try again, or email support@getroomlift.com.'); });
  }, true);

  // ---- clicks (product page + saved page)
  document.addEventListener('click', function (e) {
    var t = e.target; if (!t || !t.closest) return;
    var sv = t.closest('[data-rl-sv-save]');
    if (sv) {
      var box = sv.closest('[data-rl-sv-pd]'); if (!box) return;
      var h = box.getAttribute('data-handle');
      if (has(h)) { drop(h); pdNote(box, 'Removed from your saved pieces.'); ev('rl_save_remove'); }
      else if (load().length >= MAX) { pdNote(box, 'Your list is full (20 pieces). Remove one on <a href="/pages/saved">your saved pieces</a> first.'); return; }
      else { add({ h: h, v: variantOf(box), ts: Date.now() }); pdNote(box, savedMsg()); ev('rl_save_add'); }
      pdPaint(box); return;
    }
    var sh = t.closest('[data-rl-sv-share]');
    if (sh) {
      var bx = sh.closest('[data-rl-sv-pd]'); if (!bx) return;
      var vid = variantOf(bx), name = bx.getAttribute('data-title') || 'this piece';
      if (phone()) { ev('rl_share_tap'); shareOrSheet(name, utm(ORIGIN + '/products/' + bx.getAttribute('data-handle') + (vid ? '?variant=' + vid : ''), 'product')); return; }
      share(sh, bx.querySelector('[data-rl-sv-panel]'), utm(ORIGIN + '/products/' + bx.getAttribute('data-handle') + (vid ? '?variant=' + vid : ''), 'product'),
        'What do you think of this for our home? ' + name, name + ' - Roomlift', 'rl_share_tap');
      return;
    }
    var root = t.closest('[data-rl-sv]'); if (!root) return;
    var one = t.closest('[data-rl-sv-add]');
    if (one) {
      one.disabled = true; one.textContent = 'Adding…';
      addToCart([{ id: Number(one.getAttribute('data-rl-sv-add')), quantity: 1 }])
        .then(function () { one.textContent = 'In your cart'; msg(root, 'Added to your cart. <a href="/cart">View cart</a>'); })
        .catch(function () { one.disabled = false; one.textContent = 'Add to cart'; msg(root, 'That piece could not be added. Please try again.'); });
      return;
    }
    if (t.closest('[data-rl-sv-rm]')) {
      var li = t.closest('[data-h]'); if (li) { drop(li.getAttribute('data-h')); renderPage(root); msg(root, ''); }
      return;
    }
    var all = t.closest('[data-rl-sv-addall]');
    if (all) {
      var ok = pg.rows.filter(function (x) { return x.ok && x.available && x.id; });
      var skipped = pg.rows.filter(function (x) { return x.ok && !x.available; });
      if (!ok.length) return;
      all.disabled = true; all.textContent = 'Adding…'; ev('rl_list_addall');
      addToCart(ok.map(function (x) { return { id: x.id, quantity: 1 }; }))
        .then(function () {
          if (!skipped.length) { window.location.href = '/cart'; return; }
          all.textContent = 'Added'; msg(root, 'Added ' + ok.length + (ok.length === 1 ? ' piece' : ' pieces') + '. Sold out and skipped: ' + skipped.map(function (x) { return esc(x.title); }).join(', ') + '. <a href="/cart">View cart</a>');
        })
        .catch(function () { all.disabled = false; all.textContent = 'Add all to cart'; msg(root, 'Those pieces could not be added. Please try again.'); });
      return;
    }
    var ls = t.closest('[data-rl-sv-listshare]');
    if (ls) {
      var good = pg.rows.filter(function (x) { return x.ok; }); if (!good.length) return;
      share(ls, root.querySelector('[data-rl-sv-panel]'), utm(ORIGIN + '/pages/saved?items=' + good.map(function (x) { return x.h + '~' + (x.id || ''); }).join(','), 'list'),
        'What do you think of these for our home? ' + good.length + (good.length === 1 ? ' piece' : ' pieces') + ' from Roomlift', 'Our saved pieces - Roomlift', 'rl_list_share');
      return;
    }
    var et = t.closest('[data-rl-sv-emailtoggle]');
    if (et) {
      var w = root.querySelector('[data-rl-sv-email]'); w.hidden = !w.hidden; et.setAttribute('aria-expanded', w.hidden ? 'false' : 'true');
      if (!w.hidden) { var inp = w.querySelector('#rl-sv-email-inp'); if (inp && !w.querySelector('#rl-sv-form').hidden) { try { inp.focus({ preventScroll: true }); } catch (x) { inp.focus(); } } w.scrollIntoView({ block: 'nearest' }); }
      return;
    }
    var kp = t.closest('[data-rl-sv-keep]');
    if (kp) {
      pg.rows.forEach(function (x) { if (x.ok && !has(x.h) && load().length < MAX) add({ h: x.h, v: x.id ? String(x.id) : '', ts: Date.now() }); });
      kp.disabled = true; kp.querySelector('span').textContent = 'Saved to your list'; ev('rl_shared_keep');
      return;
    }
  });
  document.addEventListener('focusin', function (e) { if (e.target && e.target.id === 'rl-sv-email-inp') document.documentElement.classList.add('rl-sp-typing'); });
  document.addEventListener('focusout', function (e) { if (e.target && e.target.id === 'rl-sv-email-inp') document.documentElement.classList.remove('rl-sp-typing'); });
  window.addEventListener('storage', function (e) { if (e.key === KEY) { paintHead(); cardPaint(); document.querySelectorAll('[data-rl-sv-pd]').forEach(pdPaint); } });

  function boot() {
    paintHead();
    cardPaint();
    document.querySelectorAll('[data-rl-sv-pd]').forEach(pdPaint);
    var root = document.querySelector('[data-rl-sv]');
    if (!root) return;
    renderPage(root);
    // back from the bot-check fallback of "Email me this list"
    if (/[?&]contact_posted=true/.test(location.search) && sget(PEND) === location.pathname) {
      sset(PEND, null);
      var rec = null; try { rec = JSON.parse(sget(REC) || 'null'); } catch (x) {}
      sset(REC, null);
      if (rec && rec.e) record(rec.e, rec.t);
      var p = wlParts(root); p.w.hidden = false; wlDone(p);
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();

/* REVIEWS MORE (2026-10-20): "Show more reviews" on the Reviews part (.rl-rev, block rl_reviews) - fetches the next pages
   from Judge.me's public widget feed (the same feed its own widget uses; CORS *), 10 at a time, only when tapped.
   Delegated click, so it survives Horizon's section morphing. CSS = roomlift-2026.css section 70. */
(function () {
  if (window.__rlRevMore) return; window.__rlRevMore = 1;
  var SHOP = '51s1jy-bu.myshopify.com';
  function esc(s) { var d = document.createElement('div'); d.textContent = s == null ? '' : String(s); return d.innerHTML; }
  function plain(html) { var d = document.createElement('div'); d.innerHTML = html || ''; return (d.textContent || '').trim(); }
  function month(iso) { try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }); } catch (e) { return ''; } }
  // rl-reviews2 2026-10-20: never show the store or brand name (owner) - same rules as the Liquid in block rl_reviews
  function label(r) {
    var t = r.title || '', own = '', meta = r.verified_buyer ? 'Verified Roomlift buyer' : '';
    if (t.indexOf('From ') === 0) {
      var i = t.indexOf(':'), head = i >= 0 ? t.slice(0, i) : t; own = i >= 0 ? t.slice(i + 1).trim() : '';
      var p = head.indexOf('(');
      if (p >= 0) {
        head.slice(p + 1).replace(/\)\s*$/, '').replace('reviewer got store rewards for reviewing', 'incentivized review')
          .replace('incentivized review by a Walmart associate', 'incentivized review')
          .split(', ').forEach(function (x) {
            if (!x || /sold there as|Wayfair|Walmart/.test(x)) return;
            meta = meta ? meta + ' · ' + x : x;
          });
      }
    } else { own = t; }
    return { meta: meta, own: own };
  }
  function item(r) {
    var li = document.createElement('li'); li.className = 'rl-rev__item'; li.setAttribute('data-uuid', r.uuid || '');
    var n = Number(r.rating) || 0, pct = Math.max(0, Math.min(100, n * 20)), lab = label(r);
    li.innerHTML = '<span class="rl-stars" style="--rl-fill:' + pct + '%" role="img" aria-label="' + n + ' out of 5 stars"></span>' +
      (lab.meta ? '<p class="rl-rev__src">' + esc(lab.meta) + '</p>' : '') + (lab.own ? '<p class="rl-rev__ttl">' + esc(lab.own) + '</p>' : '') +
      '<p class="rl-rev__body">' + esc(plain(r.body_html)) + '</p>' +
      '<p class="rl-rev__by">' + esc(r.reviewer_name || 'Anonymous') + ' · ' + esc(month(r.created_at)) + '</p>';
    return li;
  }
  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest ? e.target.closest('[data-rl-rev-more]') : null; if (!b) return;
    var sec = b.closest('.rl-rev'); if (!sec || b.getAttribute('aria-busy') === 'true') return;
    var list = sec.querySelector('.rl-rev__list'), pid = sec.getAttribute('data-pid'), total = +sec.getAttribute('data-n') || 0;
    var page = (+sec.getAttribute('data-page') || 0) + 1;
    b.setAttribute('aria-busy', 'true');
    fetch('https://judge.me/reviews/reviews_for_widget?url=' + SHOP + '&shop_domain=' + SHOP + '&platform=shopify&per_page=10&page=' + page + '&product_id=' + encodeURIComponent(pid))
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
      .then(function (d) {
        var got = d && d.reviews ? d.reviews : [], seen = {};
        list.querySelectorAll('[data-uuid]').forEach(function (n) { seen[n.getAttribute('data-uuid')] = 1; });
        got.forEach(function (r) { if (!seen[r.uuid]) list.appendChild(item(r)); });
        sec.setAttribute('data-page', page);
        var left = total - list.children.length;
        if (left > 0 && got.length) b.textContent = 'Show more reviews (' + left + ' more)'; else b.remove();
      })
      .catch(function () { b.textContent = 'More reviews could not load. Try again'; })
      .then(function () { b.removeAttribute('aria-busy'); });
  });
  /* REVIEW FORM (2026-10-21, rl-reviews4, owner: "on every product page open the review options"): "Write a review" opens the
     inline form in the Reviews part; it posts to Judge.me's public create-review endpoint (POST https://judge.me/api/v1/reviews,
     no token - judge.me/help 8409180). Judge.me holds it until the owner publishes it (Auto-publish = manual). Delegated events,
     so it survives Horizon's section morphing. CSS = roomlift-2026.css section 70c. */
  function paint(box, v) { if (box) box.querySelectorAll('label').forEach(function (l, i) { l.classList.toggle('is-on', i < v); }); }
  document.addEventListener('click', function (e) {
    var w = e.target && e.target.closest ? e.target.closest('[data-rl-rev-write]') : null; if (!w) return;
    var sec = w.closest('.rl-rev'), f = sec && sec.querySelector('[data-rl-rev-form]'); if (!f) return;
    var open = f.hidden; f.hidden = !open; w.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) { var r = f.querySelector('input[name="rating"]'); if (r) r.focus({ preventScroll: true }); }
  });
  document.addEventListener('change', function (e) {
    var t = e.target; if (!t || t.name !== 'rating' || !t.closest || !t.closest('[data-rl-rev-form]')) return;
    paint(t.closest('.rl-rev__stars'), +t.value);
  });
  document.addEventListener('mouseover', function (e) {
    var l = e.target && e.target.closest ? e.target.closest('.rl-rev__stars label') : null; if (!l) return;
    var box = l.parentNode; paint(box, [].indexOf.call(box.querySelectorAll('label'), l) + 1);
  });
  document.addEventListener('mouseout', function (e) {
    var box = e.target && e.target.closest ? e.target.closest('.rl-rev__stars') : null; if (!box || box.contains(e.relatedTarget)) return;
    var c = box.querySelector('input:checked'); paint(box, c ? +c.value : 0);
  });
  document.addEventListener('submit', function (e) {
    var f = e.target; if (!f || !f.matches || !f.matches('[data-rl-rev-form]')) return;
    e.preventDefault();
    var sec = f.closest('.rl-rev'), msg = f.querySelector('.rl-rev__msg'), btn = f.querySelector('.rl-rev__send');
    if (!sec || btn.disabled) return;
    function val(n) { var el = f.querySelector('[name="' + n + '"]'); return el ? String(el.value || '').trim() : ''; }
    var rate = f.querySelector('input[name="rating"]:checked'), body = val('body'), name = val('name'), email = val('email');
    var bad = !rate ? 'Pick a star rating.' : body.length < 2 ? 'Write a few words about the piece.' : !name ? 'Add your name.' :
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? 'Add a valid email.' : '';
    if (bad) { msg.textContent = bad; msg.className = 'rl-rev__msg is-err'; return; }
    btn.disabled = true; btn.setAttribute('aria-busy', 'true'); msg.className = 'rl-rev__msg'; msg.textContent = 'Sending...';
    var p = new URLSearchParams({ shop_domain: SHOP, platform: 'shopify', id: sec.getAttribute('data-pid'), name: name, email: email,
      rating: rate.value, title: val('title'), body: body, reviewer_name_format: 'last_initial' });
    fetch('https://judge.me/api/v1/reviews', { method: 'POST', body: p })
      .then(function (r) {
        if (r.ok) return;
        return r.json().catch(function () { return {}; }).then(function (d) { return Promise.reject(d && d.message); });
      })
      .then(function () {
        f.innerHTML = '<p class="rl-rev__done" role="status">Thank you. Your review is in, and it will show here once we have read it.</p>';
        var w = sec.querySelector('[data-rl-rev-write]'); if (w) w.remove();
      })
      .catch(function (m) {
        msg.className = 'rl-rev__msg is-err';
        msg.textContent = (typeof m === 'string' && m ? m + '. ' : '') + 'Your review did not send. Please try again.';
        btn.disabled = false; btn.removeAttribute('aria-busy');
      });
  });
})();
